/**
 * ReviewSessionDetail - AI-enhanced findings review workflow
 *
 * Three-panel layout with collapsible panels:
 *   Left:   Findings list with severity/decision filter dropdowns (collapsible)
 *   Center: Selected finding detail with decision buttons
 *   Right:  Side-panel with Activity & Changes tabs (toggle)
 *
 * Real-time updates via PubSub for live collaboration.
 * Document viewer opens via GlobalModal.
 *
 * URL: /reviews/:reviewUuid
 */

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import BrandLoader from '../shared/brand/BrandLoader';
import { AIProgressPanel, AIRun, initialAIRun, useAIRunElapsed } from '../shared/brand/aiProgress';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Menu } from '@headlessui/react';
import { LayoutContext } from '../../contexts/LayoutContext';

import DetailFrame from '../shared/Frames/DetailFrame';
import GlassCard from '../shared/brand/GlassCard';
import ReviewSurface, { reviewRailItems, useReviewPanels } from './surface/ReviewSurface';
import PaneRail from '../shared/surface/PaneRail';
import AIUsagePanel from '../shared/aiUsage/AIUsagePanel';
import ReviewHeaderReadout from './surface/ReviewHeaderReadout';
import ReviewPlaybookBanner from './ReviewPlaybookBanner';
import {
  CONFIDENCE_EMPTY,
  CONFIDENCE_LABEL,
  CONFIDENCE_TONE,
  confidenceBand,
} from './surface/confidence';
import ReviewDocumentCanvas, { ReviewDocumentTarget } from './ReviewDocumentCanvas';
import DocumentPicker from './DocumentPicker';
import DocumentModeToggles, { DocumentActions } from './DocumentViewControls';
import { RevisionStyle, ViewSource, downloadName, findingScrollAnchor } from './documentView';
import decisionBuckets, { isFullyResolved } from './surface/decisionBuckets';
import {
  FindingTab,
  nextSearch,
  parseDocParam,
  parseFindingParam,
  parseTabParam,
} from './surface/reviewUrlState';
import { Well } from '../shared/brand/Field';
import ProjectBadges from '../Projects/ProjectBadges';
import PendingAccessRequests from '../Projects/PendingAccessRequests';
import Badge from '../shared/TailwindComponents/Badge';
import Button from '../shared/TailwindComponents/Button';
import Textarea from '../shared/TailwindComponents/Textarea';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import ProfileImage from '../Header/ProfileImage';
import { normalise } from '../shared/access/accessModel';
import { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';
import {
  PiCheckCircleLight,
  PiXCircleLight,
  PiPencilSimpleLight,
  PiArrowUpRightLight,
  PiArrowLineLeftLight,
  PiArrowLineRightLight,
  PiFunnelLight,
  PiFileTextLight,
  PiSpinnerGapLight,
  PiShareNetworkLight,
  PiClockCounterClockwiseLight,
  PiArrowsClockwiseLight,
  PiCaretLeftLight,
  PiCaretRightLight,
  PiTrashLight,
  PiCaretDownLight,
  PiSealCheckLight,
  PiDownloadSimpleLight,
  PiLockKeyLight,
  PiArrowCounterClockwiseLight,
  PiQuestionLight,
  PiFlagCheckeredLight,
  PiArrowRightLight,
  PiXLight,
  PiListChecksLight,
  PiSparkleLight,
  PiPlusCircleLight,
  PiCrosshairSimpleLight,
  PiScalesLight,
} from 'react-icons/pi';

import {
  fetchReviewSession,
  fetchFindings,
  updateFindingDecision,
  addFindingNote,
  updateFindingNote,
  deleteFindingNote,
  undoFindingDecision,
  bulkUpdateFindingDecisions,
  fetchReviewDetail,
  fetchCollaborators,
  addCollaborator as addReviewCollaborator,
  removeCollaborator as removeReviewCollaborator,
  updateCollaborator as updateReviewCollaborator,
  startReReview,
  deleteReviewSession,
  fetchSignOffStatus,
  streamReviewDocument,
  streamRevisedDocument,
  submitSignOff,
  reopenReview,
  downloadFinalDocuments,
  completeReview,
} from '../../store/actions/Review/ReviewActions';
import { showModal, hideModal } from '../../store/actions/modalActions';
import { showSidePanel } from '../../store/actions/sidePanelActions';
import {
  Finding,
  isResolvedDecision,
  FindingSeverity,
  FindingDecision,
  ReviewSessionDetail as SessionDetailType,
  SignOffStatus,
} from '../../types/review';
import { useReviewSessionPubSub } from './useReviewSessionPubSub';
import AuditLogPanel from '../AuditLog/AuditLogPanel';
import InlineDiff from './InlineDiff';
import { DocStat } from './ReviewDocumentNavigator';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, reviewSessionTourSteps } from '../shared/FeatureTour';

import ActivityDrawer from '../shared/activity/ActivityDrawer';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const severityColors: Record<FindingSeverity, BadgeColor> = {
  critical: 'red',
  major: 'orange',
  minor: 'yellow',
  info: 'blue',
};

const severityLabels: Record<FindingSeverity, string> = {
  critical: 'Critical',
  major: 'Major',
  minor: 'Minor',
  info: 'Info',
};

const decisionColors: Record<string, BadgeColor> = {
  accepted: 'green',
  rejected: 'red',
  modified: 'orange',
  escalated: 'purple',
};

const severityOrder: FindingSeverity[] = ['critical', 'major', 'minor', 'info'];

/**
 * THE section heading in the finding canvas.
 *
 * The canvas has no cards, so this label and the whitespace above it are what
 * separate one field from the next - the same job the research surface gives
 * its bucket headers and document headings. Kept as one constant so the
 * sections cannot drift apart from each other, which is how the old
 * card-per-field version started.
 */
const SECTION_LABEL =
  'text-[11px] font-semibold uppercase tracking-wide text-fg-muted mt-8 mb-2.5';

/**
 * The label over one half of the before/after pair.
 *
 * Quieter than a section heading - "Current" and "Suggested" name the two
 * panes of one block, they do not open two new sections.
 */
const PAIR_LABEL = 'text-[11px] font-medium text-fg-muted mb-1.5';

/** One thing that happened to a finding. See `findingActivity`. */
interface FindingEvent {
  key: string;
  kind: 'decision' | 'applied' | 'note';
  /** Already-formatted; translated at the render site. */
  label: string;
  who: string | null;
  at: string;
  /** Known only for notes; decision/applied events carry no ownership flag. */
  isMine?: boolean;
  /** The note this entry is about, so the row can jump to it. */
  noteUuid?: string;
  /** Which tab answers "show me this" for this kind of event. */
  target: FindingTab;
}

/**
 * Legal references, marked inline.
 *
 * PRESENTATIONAL ONLY. The engine sends prose, not structured citations, so
 * this is a reading aid over free text - it finds things shaped like a legal
 * reference ("GDPR Art. 28", "§ 5", "Section 3(1)(b)") and chips them so the
 * eye can find the authority in a paragraph. Anything it does not match is
 * left exactly as written, so a miss costs nothing and it can never drop or
 * reorder text: the split keeps every character.
 */
const REFERENCE_PATTERN =
  /((?:[A-Z]{2,10}\s)?(?:Art(?:icle|\.)|§{1,2}|Sec(?:tion|\.)|Clause|Recital)\s?\d+[A-Za-z0-9]*(?:\s?\([A-Za-z0-9]+\))*)/g;

const withReferences = (text: string): React.ReactNode => {
  const parts = text.split(REFERENCE_PATTERN);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    // Odd indices are the capture group - the matches themselves.
    i % 2 === 1 ? (
      <span
        key={i}
        data-testid="legal-reference"
        className="inline-flex items-center px-1.5 py-px mx-px rounded
                   bg-champagne/10 text-champagne text-[12px] font-medium
                   whitespace-nowrap align-baseline"
      >
        {part}
      </span>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );
};

/**
 * The chrome above the workspace, in pixels.
 *
 * The surface is sized by subtraction - it has to fill exactly what the page
 * chrome leaves, so its panes can scroll inside themselves rather than the
 * window scrolling behind them. Split in two because the halves move
 * independently: the header is the frame's, the strip is this page's.
 *
 * It was one number, 108, standing for the header plus the session summary
 * bar. That bar is gone; the strip that replaced it is `h-10` plus its
 * hairline, and it is 12px shorter - which is 12px of document.
 */
/**
 * How old a session may be and still have its `created_at` taken as the start
 * of the run in flight, when the view mounted with that run already going.
 * Beyond it, `created_at` says nothing about the CURRENT run - see the clock
 * note below.
 */
const RUN_START_FALLBACK_WINDOW_MS = 30 * 60 * 1000;

const HEADER_PX = 55;
const TOOLBAR_STRIP_PX = 41;
const CHROME_PX = HEADER_PX + TOOLBAR_STRIP_PX;

/** The small uppercase label over a metric value in the header readout. */
const METRIC_LABEL =
  'text-[10px] font-semibold uppercase tracking-wider text-fg-muted leading-none';

/**
 * THE list row, shared by both panes.
 *
 * The findings list reads well and the document list did not, and the reason
 * was not the data - it was that they were two different components that had
 * each invented their own selected state, their own hover, their own rounding
 * and their own density. The documents pane had a heavier one (a
 * primary-tinted `ring-2`) on a bigger card, so the quieter list next to it
 * looked like a different product.
 *
 * This IS the findings row - same padding, rounding, density and left-edge
 * dot slot, so both panes scan down the same line - moved onto semantic
 * tokens, plus the research surface's champagne rail as the selection marker.
 */
const LIST_ROW =
  'group relative w-full text-start px-3 py-2.5 rounded-xl mb-0.5 ' +
  'transition-all duration-200 ease-slide';
const LIST_ROW_ACTIVE = 'bg-surface shadow-md ring-1 ring-hairline';
const LIST_ROW_IDLE =
  'hover:bg-surface/80 hover:shadow-md hover:ring-1 hover:ring-hairline-soft';
/** The champagne rail marking the selected row - research's marker. */
const LIST_RAIL =
  'absolute start-0 top-2 bottom-2 w-0.5 rounded-full bg-champagne ' +
  'transition-opacity duration-200 ease-slide';

/** Convert snake_case or slug text into Title Case for display.
 *  Uses (^|\s) instead of \b to avoid treating non-ASCII characters
 *  (ä, ö, å, é …) as word boundaries. */
const formatLabel = (text: string): string =>
  text
    .replace(/_/g, ' ')
    .replace(/(^|\s)\S/g, (c) => c.toUpperCase());

/** Convert a UTC ISO string to local browser time. */
const formatUtcToLocal = (
  utcString: string | null | undefined,
  options?: Intl.DateTimeFormatOptions,
): string => {
  if (!utcString) return '';
  const date = new Date(utcString.endsWith('Z') ? utcString : utcString + 'Z');
  if (isNaN(date.getTime())) return utcString;
  return date.toLocaleString(undefined, options ?? {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// ─── Component ───────────────────────────────────────────────────────────────

const ReviewSessionDetail: React.FC = () => {
  const { reviewUuid } = useParams<{ reviewUuid: string }>();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const findingsListRef = useRef<HTMLDivElement>(null);

  /**
   * The query as it was at MOUNT - the position to restore.
   *
   * Only the arriving URL is read; every later change to it is this
   * component's own write, so re-reading would be a loop.
   *
   * react-router here is v5 (5.3.4), which has no `useSearchParams` - that is
   * a v6 hook. v5's primitives are `useLocation` to read and
   * `history.replace` to write, so the query is parsed explicitly.
   */
  const initialParams = useRef(new URLSearchParams(location.search));

  // Redux state
  const currentSession = useSelector((s: RootState) => s.review.currentSession);
  const currentReview = useSelector((s: RootState) => s.review.currentReview);
  const findings = useSelector((s: RootState) => s.review.findings);
  const isLoadingSession = useSelector((s: RootState) => s.review.isLoadingSession);
  const isLoadingFindings = useSelector((s: RootState) => s.review.isLoadingFindings);
  const userInfo = useSelector((s: RootState) => s.user.userInfo);
  const signOffStatus = useSelector((s: RootState) => s.review.signOffStatus);
  /* What the viewer has loaded - the toolbar reads its type, page count and
     overlay size from the same place the pane renders from. */
  const documentContent = useSelector((s: RootState) => s.review.documentContent);
  const isLoadingSignOff = useSelector((s: RootState) => s.review.isLoadingSignOff);

  // Local state
  /*
    Seeded from the URL at mount, not in an effect.

    The auto-select effect further down claims the first finding as soon as
    the list arrives, and an effect-based restore would race it - sometimes
    landing on the linked finding, sometimes on the first one. A lazy
    initializer is set before any effect runs, so the URL always wins and the
    auto-select only fires when there was nothing to restore.
  */
  const [selectedFindingUuid, setSelectedFindingUuid] = useState<string | null>(
    () => parseFindingParam(initialParams.current.get('finding'))
  );
  const [severityFilter, setSeverityFilter] = useState<FindingSeverity | 'all'>('all');
  const [decisionFilter, setDecisionFilter] = useState<string | 'all'>('all');
  const [decisionNotes, setDecisionNotes] = useState('');
  const [modifiedText, setModifiedText] = useState('');
  const [showModifyInput, setShowModifyInput] = useState(false);
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [editingNoteUuid, setEditingNoteUuid] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState('');

  // Document navigation state
  const [selectedDocumentId, setSelectedDocumentId] = useState<number | 'all'>(
    () => parseDocParam(initialParams.current.get('doc'))
  );

  // Panel layout state
  const [showAuditLog, setShowAuditLog] = useState(false);

  /**
   * Where the centre document should SCROLL, and how it is being read.
   *
   * There is no separate "which document is open" state any more, and that is
   * the point of this rebuild: `selectedDocumentId` - the thing the documents
   * pane sets and the findings filter reads - IS the document in the centre.
   * Two states for one idea is how the workspace ended up showing a finding
   * in the middle and the same finding in the list beside it.
   *
   * What is left here is the scroll REQUEST (which clause to jump to) and the
   * two view toggles, which live outside the canvas because the toolbar strip
   * that sets them survives the canvas being expanded to the whole window.
   */
  const [docScroll, setDocScroll] = useState<{
    findingUuid?: string;
    searchText?: string;
    sectionReference?: string;
    nonce: number;
  } | null>(null);
  const [viewSource, setViewSource] = useState<ViewSource>('original');
  const [revisionStyle, setRevisionStyle] = useState<RevisionStyle>('clean');
  /** Reading mode: the document, the toolbar, and the findings on demand. */
  const [isDocumentExpanded, setIsDocumentExpanded] = useState(false);

  /**
   * The floating-pane layout for this session.
   *
   * Owned here rather than inside the shell so the session can drive it: the
   * pane rail in the header toggles panes, and a pane can be reopened after
   * it has been closed.
   */
  const panels = useReviewPanels();

  const prevFindingsCountRef = useRef(0);
  // Tracks whether the initial auto-select has already run - prevents re-triggering
  // when the user manually clicks "All Documents" to return to the overview.
  const hasAutoSelectedDocRef = useRef(false);

  // Lock outer page scroll so internal panels handle scrolling instead
  const { setIsContentScrollable } = useContext(LayoutContext);
  useEffect(() => {
    setIsContentScrollable(false);
    return () => setIsContentScrollable(true);
  }, [setIsContentScrollable]);

  // Tour
  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'review-session',
    steps: reviewSessionTourSteps,
    readyToShow: !isLoadingSession && !!currentSession && findings.length > 0,
    autoStartDelay: 600,
  });

  /**
   * Decided / escalated / pending - the ONE place the session's progress is
   * counted, so the toolbar, the documents summary and the completion dialog
   * cannot disagree. Prefers the server's numbers and corrects a legacy
   * summary that still folds escalated into decided. See `decisionBuckets`.
   */
  const buckets = useMemo(
    () => decisionBuckets(findings, currentSession?.decision_progress),
    [findings, currentSession?.decision_progress],
  );

  // ─── Finalization computed values ──────────────────────────────────────
  const isFinalized = currentSession?.status === 'finalized';
  const isSignOffPending = currentSession?.status === 'pending_sign_off';
  const decisionsLocked = isFinalized;
  /*
    "All decided" means nothing is OPEN - escalated included. It used to read
    `pending === 0`, which went true with escalated findings still
    outstanding and put the ready-to-complete banner on screen.
  */
  const allDecided = isFullyResolved(buckets);

  // Check if current user can sign off (is a required signer and hasn't signed yet)
  const canSignOff = useMemo(() => {
    if (!signOffStatus?.signers || !userInfo?.UserUID) return false;
    const mySigner = signOffStatus.signers.find(
      (s) => s.user_uuid === userInfo.UserUID
    );
    return mySigner ? !mySigner.signed_off : false;
  }, [signOffStatus, userInfo?.UserUID]);

  const hasSignedOff = useMemo(() => {
    if (!signOffStatus?.signers || !userInfo?.UserUID) return false;
    const mySigner = signOffStatus.signers.find(
      (s) => s.user_uuid === userInfo.UserUID
    );
    return mySigner?.signed_off ?? false;
  }, [signOffStatus, userInfo?.UserUID]);

  // ─── Real-time PubSub connection ─────────────────────────────────────
  // Always connect for live collaboration (not just during processing)
  const isProcessing = currentSession?.status === 'processing' || currentSession?.status === 'pending';
  const playbookUuid = currentSession?.playbook_uuid ?? undefined;
  useReviewSessionPubSub(reviewUuid, playbookUuid, true);

  // ─── Dynamic height offset for banners above scrollable panels ────────
  // Only one status banner shows at a time; compute extra px consumed.
  const bannerOffset = useMemo(() => {
    let offset = 0;
    if (isProcessing) offset += 40;
    if (isFinalized) offset += 48;
    else if (isSignOffPending) offset += 52;
    else if (allDecided && (currentSession?.status === 'completed' || currentSession?.status === 'completed_with_errors')) offset += 44;
    return offset;
  }, [isProcessing, isFinalized, isSignOffPending, allDecided, currentSession?.status]);

  // ─── Data loading ──────────────────────────────────────────────────────

  // Clear stale data when navigating to a different session
  useEffect(() => {
    dispatch({ type: 'REVIEW_SET_SESSION', payload: null });
    dispatch({ type: 'REVIEW_SET_FINDINGS', payload: { findings: [], total: 0, page: 1 } });
    dispatch({ type: 'REVIEW_SET_SIGN_OFF_STATUS', payload: null });
    setSelectedFindingUuid(null);
    setSelectedDocumentId('all');
    setDecisionNotes('');
    setModifiedText('');
    setShowModifyInput(false);
    // A document belongs to the session it was opened from - carrying it into
    // the next one would render another session's file under this name.
    setDocScroll(null);
    setViewSource('original');
    setRevisionStyle('clean');
    setIsDocumentExpanded(false);
    hasAutoSelectedDocRef.current = false;
  }, [dispatch, reviewUuid]);

  useEffect(() => {
    if (reviewUuid) {
      dispatch(fetchReviewSession(reviewUuid, reviewUuid) as any);
      dispatch(fetchFindings(reviewUuid, reviewUuid, { page: 1, page_size: 1000 }) as any);
    }
  }, [dispatch, reviewUuid]);

  // Load parent playbook detail once we know the playbook UUID
  useEffect(() => {
    if (playbookUuid && !currentReview) {
      dispatch(fetchReviewDetail(playbookUuid) as any);
    }
  }, [dispatch, playbookUuid, currentReview]);

  // Fetch sign-off status when session is in sign-off or finalized state
  useEffect(() => {
    if (reviewUuid && currentSession && ['pending_sign_off', 'finalized'].includes(currentSession.status)) {
      dispatch(fetchSignOffStatus(reviewUuid) as any);
    }
  }, [dispatch, reviewUuid, currentSession?.status]);

  // Auto-select first non-failed document once processing is complete.
  // The ref guard ensures this only runs once on initial load - clicking
  // "All Documents" to return to the overview won't trigger a re-select.
  useEffect(() => {
    if (
      !hasAutoSelectedDocRef.current &&
      selectedDocumentId === 'all' &&
      !isProcessing &&
      currentSession?.documents &&
      currentSession.documents.length > 0
    ) {
      const firstDoc = currentSession.documents.find((d) => d.status !== 'failed');
      if (firstDoc) {
        hasAutoSelectedDocRef.current = true;
        setSelectedDocumentId(firstDoc.id);
        setSelectedFindingUuid(null);
      }
    }
  }, [currentSession?.documents, isProcessing, selectedDocumentId]);

  // Auto-scroll as findings stream in - but only "stick" to the bottom when the
  // user is already there. If they've scrolled up to read a finding, leave their
  // position alone (no yanking). New items tick in smoothly.
  useEffect(() => {
    if (findings.length > prevFindingsCountRef.current && isProcessing) {
      const listEl = findingsListRef.current;
      if (listEl) {
        const distanceFromBottom =
          listEl.scrollHeight - listEl.scrollTop - listEl.clientHeight;
        const NEAR_BOTTOM_PX = 120; // treat "within 120px of bottom" as pinned
        if (distanceFromBottom <= NEAR_BOTTOM_PX) {
          requestAnimationFrame(() => {
            listEl.scrollTo({ top: listEl.scrollHeight, behavior: 'smooth' });
          });
        }
      }
    }
    prevFindingsCountRef.current = findings.length;
  }, [findings.length, isProcessing]);

  // ─── Per-document statistics (computed client-side from loaded findings) ────

  const docStats = useMemo(() => {
    const stats: Record<number, DocStat> = {};
    for (const f of findings) {
      if (f.document_id === null) continue;
      if (!stats[f.document_id]) {
        stats[f.document_id] = { total: 0, decided: 0, escalated: 0, bySeverity: {}, maxRisk: null };
      }
      stats[f.document_id].total += 1;
      // Escalated is OPEN: it must not fill a document's progress ring or
      // mark it complete. See `isResolvedDecision`.
      if (isResolvedDecision(f.decision)) stats[f.document_id].decided += 1;
      if (f.decision === 'escalated') {
        stats[f.document_id].escalated = (stats[f.document_id].escalated || 0) + 1;
      }
      stats[f.document_id].bySeverity[f.severity] =
        (stats[f.document_id].bySeverity[f.severity] || 0) + 1;
      // The WORST risk in the document, not the average - averaging a 9 with
      // eleven 2s hides the 9, and the 9 is why you open the document.
      if (f.risk_score !== null && f.risk_score !== undefined) {
        const current = stats[f.document_id].maxRisk;
        if (current === null || current === undefined || f.risk_score > current) {
          stats[f.document_id].maxRisk = f.risk_score;
        }
      }
    }
    return stats;
  }, [findings]);

  /**
   * The numbers the "All documents" row summarises.
   *
   * `decision_progress` is the session's own count and is authoritative when
   * present; the findings currently loaded are the fallback, so the row still
   * reads correctly before that arrives. Documents-complete has no
   * server-side equivalent, so it is counted from `docStats`.
   */
  const sessionTotals = useMemo(() => {
    const documentsComplete = (currentSession?.documents || []).filter((d) => {
      const stat = docStats[d.id];
      // "Complete" means every finding ANSWERED - an escalated one leaves the
      // document open, however few are left.
      return !!stat && stat.total > 0 && stat.decided >= stat.total;
    }).length;

    return { ...buckets, documentsComplete };
  }, [buckets, currentSession?.documents, docStats]);

  // ─── Filtered findings ─────────────────────────────────────────────────

  const filteredFindings = useMemo(() => {
    let result = findings;
    // Document filter first
    if (selectedDocumentId !== 'all') {
      result = result.filter((f) => f.document_id === selectedDocumentId);
    }
    if (severityFilter !== 'all') {
      result = result.filter((f) => f.severity === severityFilter);
    }
    if (decisionFilter !== 'all') {
      if (decisionFilter === 'pending') {
        result = result.filter((f) => !f.decision);
      } else {
        result = result.filter((f) => f.decision === decisionFilter);
      }
    }
    return result;
  }, [findings, severityFilter, decisionFilter, selectedDocumentId]);

  // Findings scoped to the currently-selected document only (no severity /
  // decision filter). The filter dropdown count badges must count from THIS,
  // not the full findings array - otherwise the badges show firm-wide totals
  // (e.g. 198 pending) while the rendered list only shows the selected
  // document's items (e.g. 3). See the filter badges below.
  const scopedFindings = useMemo(
    () =>
      selectedDocumentId === 'all'
        ? findings
        : findings.filter((f) => f.document_id === selectedDocumentId),
    [findings, selectedDocumentId],
  );

  const selectedFinding = useMemo(
    () => findings.find((f) => f.uuid === selectedFindingUuid) || null,
    [findings, selectedFindingUuid],
  );

  /**
   * Which tab of the finding is open.
   *
   * Per FINDING, not per session: moving to the next finding should start on
   * its reasoning, not on whatever tab the last one happened to be left on.
   */
  const [findingTab, setFindingTab] = useState<FindingTab>(
    () => parseTabParam(initialParams.current.get('tab'))
  );

  /*
    Moving to a DIFFERENT finding resets the tab; restoring one does not.

    Without the ref guard this effect would fire on mount and stamp
    'analysis' over a restored `tab=notes` immediately.
  */
  const tabFindingRef = useRef(selectedFindingUuid);
  useEffect(() => {
    if (tabFindingRef.current === selectedFindingUuid) return;
    tabFindingRef.current = selectedFindingUuid;
    setFindingTab('analysis');
  }, [selectedFindingUuid]);

  /**
   * Everything `ProfileImage` needs for one person, resolved best-effort.
   *
   * A note carries `author_id` (an integer), `author_name` and `is_mine` —
   * and NO email or avatar URL. `ProfileImage`'s chain is
   * profile_picture → Gravatar (needs an email) → initials, so a note on its
   * own can only ever produce initials. Two sources fill the gap:
   *
   *   • the note is mine        → the signed-in user's own profile
   *   • the author is a session collaborator → their `avatar_url` + `email`
   *
   * The collaborator join is BY NAME, because `author_id` is an integer and
   * `ReviewCollaborator.user_id` is a uuid string - they are not the same
   * key, so there is nothing to join on properly. Two collaborators with
   * identical display names would resolve to the first; the fallback is a
   * correct set of initials, never a wrong face for a matched user.
   *
   * The real fix is on the API: `FindingNote` should carry the author's
   * email and avatar URL (or a uuid matching `collaborator.user_id`). Noted
   * rather than faked.
   */
  const authorIdentity = useCallback((authorName: string | null, isMine?: boolean) => {
    const myName = [userInfo?.FirstName, userInfo?.LastName].filter(Boolean).join(' ').trim();
    const norm = (s?: string | null) => (s || '').trim().toLowerCase();

    if (isMine || (myName && norm(authorName) === norm(myName))) {
      return {
        profilePicture: userInfo?.profile_picture || userInfo?.ProfilePicture || null,
        email: userInfo?.Email || null,
        name: myName || authorName || '',
      };
    }

    const match = (currentSession?.collaborators || []).find(
      (c) => norm(c.name) && norm(c.name) === norm(authorName)
    );
    return {
      profilePicture: match?.avatar_url || null,
      email: match?.email || null,
      name: match?.name || authorName || '',
    };
  }, [userInfo, currentSession?.collaborators]);

  /** Initials from a display name - the last link in the avatar chain. */
  const initialsFor = useCallback((name?: string | null) => (
    (name || '?')
      .split(' ')
      .map((n) => n[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase()
  ), []);

  /**
   * The note an activity entry sent us to, briefly marked.
   *
   * Landing on the Notes tab with twelve notes on it and no indication which
   * one you clicked is not an answer to the question. Cleared on a timer, so
   * the mark reads as "this one" rather than as a second selection state.
   */
  const [highlightedNote, setHighlightedNote] = useState<string | null>(null);

  /**
   * Follow an activity entry.
   *
   * Goes through the SAME state the URL reflects, so the jump is recorded in
   * the query string and the resulting link is shareable - rather than being
   * a private scroll that a refresh would forget.
   *
   * The activity list is derived from the SELECTED finding, so every entry
   * targets the finding already open; `findingUuid` is threaded through
   * anyway so a cross-finding feed would need no change here beyond the data
   * carrying a target.
   */
  const goToActivity = useCallback((event: FindingEvent, findingUuid: string) => {
    setSelectedFindingUuid(findingUuid);
    setFindingTab(event.target);
    setHighlightedNote(event.noteUuid || null);
  }, []);

  /* Bring the marked note into view once its tab is actually showing. */
  useEffect(() => {
    if (!highlightedNote || findingTab !== 'notes') return;
    const node = document.getElementById(`note-${highlightedNote}`);
    node?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    const timer = setTimeout(() => setHighlightedNote(null), 2400);
    return () => clearTimeout(timer);
  }, [highlightedNote, findingTab]);

  /**
   * The finding's own history.
   *
   * Derived from the finding's REAL fields - the decision, the applied
   * modification, and each note - because that is the whole of what the API
   * gives per finding. The session audit log is a separate, paginated
   * resource keyed by entity uuid; filtering whatever page of it happens to
   * be loaded would show "no activity" for a finding whose entries are on
   * page 3, which is worse than not offering it. So nothing here is invented
   * and nothing is half-shown.
   */
  const findingActivity = useMemo((): FindingEvent[] => {
    if (!selectedFinding) return [];
    const events: FindingEvent[] = [];

    if (selectedFinding.decision && selectedFinding.decision_at) {
      events.push({
        key: 'decision',
        kind: 'decision',
        label: formatLabel(selectedFinding.decision),
        who: selectedFinding.decision_by_name,
        at: selectedFinding.decision_at,
        target: 'analysis',
      });
    }
    if (selectedFinding.applied_at) {
      events.push({
        key: 'applied',
        kind: 'applied',
        label: 'Modification applied',
        who: selectedFinding.decision_by_name,
        at: selectedFinding.applied_at,
        target: 'analysis',
      });
    }
    (selectedFinding.notes || []).forEach((note) => {
      if (!note.created_at) return;
      events.push({
        key: `note-${note.uuid}`,
        kind: 'note',
        label: 'Note added',
        who: note.author_name,
        at: note.created_at,
        isMine: !!note.is_mine,
        noteUuid: note.uuid,
        target: 'notes',
      });
    });

    // Newest first - the last thing that happened is the thing being asked about.
    return events.sort(
      (a, b) => new Date(b.at).getTime() - new Date(a.at).getTime()
    );
  }, [selectedFinding]);

  /*
    ── Guards ────────────────────────────────────────────────────────

    A uuid or id in the URL is an ASSERTION, not a fact: links get shared
    between sessions, findings get deleted, documents get removed. Each is
    checked against the loaded data once that data exists and dropped if it
    does not match - which hands the selection back to the defaults below.

    Both wait for a non-empty set before judging, so a value is never
    discarded merely because the fetch has not landed yet.
  */
  useEffect(() => {
    if (selectedDocumentId === 'all') return;
    const documents = currentSession?.documents;
    if (!documents || documents.length === 0) return;
    if (!documents.some((d) => d.id === selectedDocumentId)) {
      setSelectedDocumentId('all');
    }
  }, [currentSession?.documents, selectedDocumentId]);

  useEffect(() => {
    if (!selectedFindingUuid || findings.length === 0) return;
    if (!findings.some((f) => f.uuid === selectedFindingUuid)) {
      // Cleared, not defaulted - the auto-select below then picks the first
      // finding of whatever view the user is actually looking at.
      setSelectedFindingUuid(null);
    }
  }, [findings, selectedFindingUuid]);

  /*
    NOTHING is auto-selected.

    The findings pane is a list that becomes a detail when you choose a row,
    so auto-selecting the first finding would drop every arrival straight into
    one finding's detail with the list hidden behind a back button - the
    opposite of what landing on a review should show you. A `?finding=` in the
    URL still opens its detail, because that was an explicit ask.
  */

  /*
    ── Reflect ───────────────────────────────────────────────────────

    `replace`, never `push`: clicking through twenty findings is one position
    being refined, not twenty pages visited. Pushing would bury the page you
    arrived from under a stack of your own clicks and make Back useless.

    Params the component does not own are carried through untouched, and the
    write is skipped when the string is already correct - otherwise this
    effect would re-enter on the location change it just caused.
  */
  useEffect(() => {
    const search = nextSearch(location.search, {
      document: selectedDocumentId,
      finding: selectedFindingUuid,
      tab: findingTab,
    });
    if (search === location.search.replace(/^\?/, '')) return;
    history.replace({ pathname: location.pathname, search: search ? `?${search}` : '' });
  }, [
    selectedDocumentId, selectedFindingUuid, findingTab,
    history, location.pathname, location.search,
  ]);

  // Sync note editor with the currently-selected finding's saved notes
  useEffect(() => {
    setDecisionNotes('');
    setEditingNoteUuid(null);
    setEditingContent('');
  }, [selectedFindingUuid]);

  // Count pending findings in the current filtered view (for bulk actions)
  const pendingFilteredFindings = useMemo(
    () => filteredFindings.filter((f) => !f.decision),
    [filteredFindings],
  );

  // ─── Document focus state ────────────────────────────────────────────────

  const selectedDocStat =
    selectedDocumentId === 'all' ? undefined : docStats[selectedDocumentId];
  const docAllDecided = !!(selectedDocStat && selectedDocStat.total > 0 && selectedDocStat.decided === selectedDocStat.total);

  const nextPendingDocId = useMemo(() => {
    if (!currentSession?.documents) return null;
    return (
      currentSession.documents.find((d) => {
        if (d.id === selectedDocumentId || d.status === 'failed') return false;
        const stat = docStats[d.id];
        return !stat || stat.total === 0 || stat.decided < stat.total;
      })?.id ?? null
    );
  }, [currentSession?.documents, selectedDocumentId, docStats]);

  // ─── Put a document in the centre ──────────────────────────────────────

  /**
   * Showing a document IS selecting it.
   *
   * Every way in lands here: a document row, a finding's document link, and
   * selecting a finding that belongs to another document. Each one moves the
   * centre - there is no second "opened document" to fall out of step with
   * the one the findings are filtered to.
   */
  const showDocument = useCallback((
    docId: number | null,
    scrollToFindingUuid?: string,
    scrollSearchText?: string,
    scrollSectionReference?: string,
  ) => {
    if (!docId) return;
    setSelectedDocumentId(docId);
    setDocScroll((prev) => ({
      findingUuid: scrollToFindingUuid,
      searchText: scrollSearchText,
      sectionReference: scrollSectionReference,
      /* Asking for the same clause twice is a real request - the highlight
         fades, so the viewer needs to hear the second ask. */
      nonce: (prev?.nonce ?? 0) + 1,
    }));
  }, []);

  // ─── Decision handling ─────────────────────────────────────────────────

  const handleDecision = useCallback(async (decision: NonNullable<FindingDecision>) => {
    if (!selectedFinding || !reviewUuid) return;

    const payload: any = {
      decision,
      notes: decisionNotes || undefined,
    };

    // Include modified text for 'modified' decisions
    if (decision === 'modified' && modifiedText.trim()) {
      payload.modified_text = modifiedText.trim();
    }

    await dispatch(
      updateFindingDecision(reviewUuid, reviewUuid, selectedFinding.uuid, payload) as any,
    );

    setDecisionNotes('');
    setModifiedText('');
    setShowModifyInput(false);

    /*
      SHOW THE REVIEWER WHAT THE DECISION DID.

      Accepting re-renders the revised document from the server (see
      `revisionToken`), and the reload used to drop the reader at the top of it
      with nothing marked - so a decision that changed a clause three pages
      down looked like it had gone nowhere. The document is pointed back at
      THIS finding the moment the decision lands; the viewer re-fires that
      target at the reloaded frame, scrolls to it and highlights it.

      The search text is the text that will BE there afterwards IN THE VIEW
      THE REVIEWER IS READING, which is not one string: on the clean revision
      an accepted finding's original wording is gone, and its injected
      `finding-<uuid>` anchor with it, so `original_text` would match nothing —
      while on the original document, and on the redline where the struck-out
      original is still on the page, the replacement text is what matches
      nothing. `findingScrollAnchor` picks per view; the section reference is
      the fallback that holds in all of them.
    */
    if (selectedFinding.document_id) {
      const anchor = findingScrollAnchor(
        selectedFinding,
        viewSource,
        revisionStyle,
        { decision, modifiedText: payload.modified_text },
      );
      showDocument(
        selectedFinding.document_id,
        selectedFinding.uuid,
        anchor.searchText,
        anchor.sectionReference,
      );
    }

    // Advance to next undecided finding
    const currentIdx = filteredFindings.findIndex((f) => f.uuid === selectedFinding.uuid);
    const nextUndecided = filteredFindings.find(
      (f, i) => i > currentIdx && !f.decision
    );
    if (nextUndecided) {
      setSelectedFindingUuid(nextUndecided.uuid);
    }
  }, [dispatch, reviewUuid, selectedFinding, decisionNotes, modifiedText, filteredFindings,
      showDocument, viewSource, revisionStyle]);

  // ─── Undo decision ──────────────────────────────────────────────────────

  const handleUndoDecision = useCallback(async () => {
    if (!selectedFinding || !reviewUuid || !selectedFinding.decision) return;
    await dispatch(undoFindingDecision(reviewUuid, selectedFinding.uuid) as any);
  }, [dispatch, reviewUuid, selectedFinding]);

  // ─── Finding notes (multi-note thread) ──────────────────────────────────

  const handleAddNote = useCallback(async () => {
    if (!selectedFinding || !reviewUuid || !decisionNotes.trim()) return;
    setIsSavingNotes(true);
    try {
      const result = await dispatch(
        addFindingNote(reviewUuid, selectedFinding.uuid, decisionNotes.trim()) as any,
      );
      if (result) setDecisionNotes('');
    } finally {
      setIsSavingNotes(false);
    }
  }, [dispatch, reviewUuid, selectedFinding, decisionNotes]);

  const handleUpdateNote = useCallback(
    async (noteUuid: string, content: string) => {
      if (!selectedFinding || !reviewUuid || !content.trim()) return false;
      setIsSavingNotes(true);
      try {
        const result = await dispatch(
          updateFindingNote(reviewUuid, selectedFinding.uuid, noteUuid, content.trim()) as any,
        );
        return !!result;
      } finally {
        setIsSavingNotes(false);
      }
    },
    [dispatch, reviewUuid, selectedFinding],
  );

  const handleDeleteNote = useCallback(
    async (noteUuid: string) => {
      if (!selectedFinding || !reviewUuid) return;
      setIsSavingNotes(true);
      try {
        await dispatch(deleteFindingNote(reviewUuid, selectedFinding.uuid, noteUuid) as any);
      } finally {
        setIsSavingNotes(false);
      }
    },
    [dispatch, reviewUuid, selectedFinding],
  );

  // ─── Bulk accept/reject handlers ─────────────────────────────────────────────

  const handleBulkDecision = useCallback((decision: 'accepted' | 'rejected') => {
    if (!reviewUuid || pendingFilteredFindings.length === 0) return;

    const count = pendingFilteredFindings.length;
    const uuids = pendingFilteredFindings.map((f) => f.uuid);
    const label = decision === 'accepted' ? t('accept') : t('reject');
    const Label = decision === 'accepted' ? t('Accept') : t('Reject');

    // Build context about what's being bulk-acted on
    const filterDesc = severityFilter !== 'all'
      ? ` ${severityLabels[severityFilter as FindingSeverity]?.toLowerCase() || severityFilter}`
      : '';

    const confirmMessage = t(
      'Are you sure you want to {{label}} {{count}}{{filter}} pending findings? This action will apply to all undecided findings in the current view.',
      { label, count, filter: filterDesc },
    );

    dispatch(
      showModal({
        type: 'confirmation',
        message: `${Label} ${count} Findings`,
        content: confirmMessage,
        showModal: true,
        onConfirm: async () => {
          dispatch(hideModal());
          await dispatch(bulkUpdateFindingDecisions(reviewUuid, decision, uuids) as any);
        },
        onCancel: () => dispatch(hideModal()),
      }),
    );
  }, [dispatch, reviewUuid, pendingFilteredFindings, severityFilter, t]);


  /**
   * The document the canvas is rendering.
   *
   * Derived, never stored: the session's own document list is the source of
   * the name, so a rename cannot leave a stale title in the middle of the
   * page. Null while the session is on "all documents" - the canvas draws its
   * empty state and asks for one.
   */
  const centerDocument = useMemo((): ReviewDocumentTarget | null => {
    if (selectedDocumentId === 'all') return null;
    const doc = currentSession?.documents?.find((d) => d.id === selectedDocumentId);
    if (!doc) return null;
    return {
      documentId: doc.id,
      documentName: doc.file_name || t('Document'),
      scrollToFindingUuid: docScroll?.findingUuid,
      scrollSearchText: docScroll?.searchText,
      scrollSectionReference: docScroll?.sectionReference,
      scrollNonce: docScroll?.nonce,
    };
  }, [selectedDocumentId, currentSession?.documents, docScroll, t]);

  /**
   * Selecting a finding - from the list, or by clicking its clause.
   *
   * The document follows the finding: choosing one whose clause is in another
   * document moves the centre to that document, so "show me this finding"
   * always means the finding AND the text it is about.
   */
  const selectFinding = useCallback((finding: Finding) => {
    setSelectedFindingUuid(finding.uuid);
    setDecisionNotes('');
    setShowModifyInput(false);
    setModifiedText('');
    if (finding.document_id) {
      const anchor = findingScrollAnchor(finding, viewSource, revisionStyle);
      showDocument(
        finding.document_id,
        finding.uuid,
        anchor.searchText,
        anchor.sectionReference,
      );
    }
  }, [showDocument, viewSource, revisionStyle]);

  /**
   * "Show me this one in the document" - on demand, not only on accept.
   *
   * The jump used to exist in exactly one place: the moment a decision
   * landed. A reviewer who wanted to re-read the clause behind a finding they
   * had already decided - or one they had not decided yet - had to scroll the
   * document by hand and find it, which is the work the anchors were injected
   * to remove.
   *
   * It is the same request `handleDecision` makes, aimed with the same
   * `findingScrollAnchor`, so it lands on whichever version is being read:
   * the original's untouched clause, the redline's struck-out one, or the
   * clean revision's replacement. Selecting first means the findings pane and
   * the document agree about which finding is being looked at.
   */
  const locateFinding = useCallback((finding: Finding) => {
    if (!finding.document_id) return;
    setSelectedFindingUuid(finding.uuid);
    const anchor = findingScrollAnchor(finding, viewSource, revisionStyle);
    showDocument(
      finding.document_id,
      finding.uuid,
      anchor.searchText,
      anchor.sectionReference,
    );
  }, [showDocument, viewSource, revisionStyle]);

  /** A clause was clicked in the document: open its finding in the pane. */
  const handleFindingSelectFromDocument = useCallback((findingUuid: string) => {
    const match = findings.find((f) => f.uuid === findingUuid);
    if (match) selectFinding(match);
  }, [findings, selectFinding]);

  /**
   * Save the document as it is currently being READ.
   *
   * Streamed again rather than reaching into the viewer for its blob: the
   * toolbar lives outside the pane precisely so it survives the pane, and a
   * handle into a component that may be unmounted is not a handle. The
   * decrypted bytes never touch a URL - same path the viewer uses.
   */
  const handleDownloadDocument = useCallback(async () => {
    if (!centerDocument || !reviewUuid) return;
    const result = viewSource === 'revised'
      ? await dispatch(streamRevisedDocument(reviewUuid, centerDocument.documentId, revisionStyle) as any)
      : await dispatch(streamReviewDocument(reviewUuid, centerDocument.documentId) as any);
    if (!result?.success || !result.blob) return;

    const url = URL.createObjectURL(result.blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = downloadName(centerDocument.documentName, viewSource, revisionStyle);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [dispatch, reviewUuid, centerDocument, viewSource, revisionStyle]);

  /** `DOCX · 12p` - what the file IS, shown beside its name. */
  const fileInfo = useMemo(() => {
    const type = (documentContent?.file_type || '').toUpperCase();
    const pages = documentContent?.page_count ? `${documentContent.page_count}p` : '';
    return [type, pages].filter(Boolean).join(' · ') || null;
  }, [documentContent?.file_type, documentContent?.page_count]);

  /**
   * The document's name, as the way to choose a different one.
   *
   * Documents used to be a docked pane - a permanent column spent on a choice
   * you make occasionally, taking width from the text you read constantly.
   * The list is identical; it just hangs off the name in the header, which is
   * where a reader already looks to know what they are reading.
   */
  const documentPicker = useMemo(() => {
    if (!currentSession?.documents || currentSession.documents.length === 0) return null;
    return (
      <DocumentPicker
        documents={currentSession.documents}
        docStats={docStats}
        selectedDocumentId={selectedDocumentId}
        documentName={centerDocument?.documentName ?? null}
        fileInfo={fileInfo}
        totals={sessionTotals}
        allDecided={allDecided}
        finalized={currentSession.status === 'finalized'}
        onSelectDocument={(id) => { setSelectedDocumentId(id); setSelectedFindingUuid(null); }}
        onSelectAll={() => { setSelectedDocumentId('all'); setSelectedFindingUuid(null); }}
      />
    );
  }, [
    currentSession?.documents, currentSession?.status, docStats, selectedDocumentId,
    centerDocument?.documentName, fileInfo, sessionTotals, allDecided,
  ]);

  /** Which version of the document - the middle of the bar, beside the name. */
  const documentModeToggles = useMemo(() => {
    if (!centerDocument) return null;
    return (
      <DocumentModeToggles
        fileType={documentContent?.file_type}
        viewSource={viewSource}
        onViewSourceChange={setViewSource}
        revisionStyle={revisionStyle}
        onRevisionStyleChange={setRevisionStyle}
      />
    );
  }, [centerDocument, documentContent?.file_type, viewSource, revisionStyle]);

  /** What you can do with it - the far right of the bar. */
  const documentActions = useCallback(() => {
    if (!centerDocument) return null;
    return (
      <DocumentActions
        onDownload={handleDownloadDocument}
        expanded={isDocumentExpanded}
        onToggleExpand={() => setIsDocumentExpanded((v) => !v)}
      />
    );
  }, [centerDocument, handleDownloadDocument, isDocumentExpanded]);

  /*
    Esc leaves reading mode.

    Registered unconditionally and no-ops when not expanded, so it is one
    hook in one order on every render - the early-return hook bug this file
    already had once came from doing the opposite.
  */
  useEffect(() => {
    if (!isDocumentExpanded) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsDocumentExpanded(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isDocumentExpanded]);

  // ─── Share handler ─────────────────────────────────────────────────────

  const handleShare = useCallback(() => {
    if (!reviewUuid) return;
    dispatch(showSidePanel({
      type: 'manageResourceCollaborators',
      showSidePanel: true,
      size: 'md',
      message: t('Manage Access'),
      resourceUuid: reviewUuid,
      resourceType: 'review_session',
      collaborators: currentSession?.collaborators || [],
      currentUserUuid: userInfo?.UserUID,
      canAdmin: true,
      addCollaborator: async (uuid: string, userUuid: string, access: string) => {
        await dispatch(addReviewCollaborator(uuid, { collaborator_id: userUuid, access: access as any }) as any);
      },
      removeCollaborator: async (uuid: string, collabUuid: string) => {
        await dispatch(removeReviewCollaborator(uuid, collabUuid) as any);
      },
      updateAccess: async (uuid: string, collabUuid: string, access: string) => {
        await dispatch(updateReviewCollaborator(uuid, collabUuid, { access: access as any }) as any);
      },
      onUpdate: async () => {
        await dispatch(fetchCollaborators(reviewUuid) as any);
        dispatch(fetchReviewSession(reviewUuid, reviewUuid) as any);
      },
    }));
  }, [dispatch, reviewUuid, currentSession?.collaborators, userInfo?.UserUID, t]);

  // ─── Navigate to finding from audit log ─────────────────────────────────

  const handleFindingSelectFromLog = useCallback((findingUuid: string) => {
    setSelectedFindingUuid(findingUuid);
    setDecisionNotes('');
    setShowModifyInput(false);
    setModifiedText('');
  }, []);

  // ─── Re-review handler ─────────────────────────────────────────────────

  const handleReReview = useCallback(async () => {
    if (!reviewUuid) return;
    const result = await dispatch(startReReview(reviewUuid, { files: [] }) as any);
    if (result?.session_uuid) {
      history.push(`/reviews/${result.session_uuid}`);
    }
  }, [dispatch, reviewUuid, history]);

  // ─── Sign-off handler ──────────────────────────────────────────────────

  const handleSignOff = useCallback(async () => {
    if (!reviewUuid) return;
    dispatch(
      showModal({
        type: 'confirmation',
        message: t('Sign off on this review?'),
        content: t('This confirms that you have reviewed all findings and agree with the decisions made. Once all required signers have signed off, the review will be finalized.'),
        icon: <PiSealCheckLight className='w-4 h-4' />,
        showModal: true,
        onConfirm: async () => {
          dispatch(hideModal());
          await dispatch(submitSignOff(reviewUuid) as any);
        },
        onCancel: () => dispatch(hideModal()),
      }),
    );
  }, [dispatch, reviewUuid, t]);

  // ─── Complete review handler ────────────────────────────────────────────

  const handleCompleteReview = useCallback(() => {
    if (!reviewUuid || !currentSession) return;

    /*
      WARN AND ALLOW.

      Completing with open findings stays permitted - sometimes that is a
      deliberate call. What changed is that the dialog now says what is being
      accepted, and escalated findings are named SEPARATELY: they used to be
      counted as reviewed, so a session with three findings waiting on a
      partner announced that everything had been reviewed.
    */
    const { total, decided, escalated, pending } = buckets;

    // Severity breakdown of what is still open - untouched findings only.
    // An escalated finding is described by its own sentence, not folded into
    // a severity tally that reads as "nobody has looked at these".
    const pendingBySeverity = findings
      .filter((f) => !f.decision)
      .reduce((acc, f) => {
        acc[f.severity] = (acc[f.severity] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

    const severityLine = Object.entries(pendingBySeverity)
      .map(([sev, count]) => `${count} ${sev}`)
      .join(', ');

    const lines: string[] = [];
    if (escalated === 0 && pending === 0) {
      lines.push(t('All {{total}} findings have been reviewed.', { total }));
    } else {
      lines.push(t('You have decided {{decided}} of {{total}} findings.', { decided, total }));
      if (escalated > 0) {
        lines.push(
          t('{{count}} findings are escalated and unresolved — nobody has decided them yet.', {
            count: escalated,
          })
        );
      }
      if (pending > 0) {
        lines.push(
          severityLine
            ? t('{{count}} findings are still undecided ({{severity}}).', { count: pending, severity: severityLine })
            : t('{{count}} findings are still undecided.', { count: pending })
        );
      }
      lines.push(t('Completing now treats every unresolved finding as an accepted risk.'));
    }
    lines.push(t('Mark this review as complete and move to the sign-off stage?'));

    const confirmMessage = lines.join(' ');

    dispatch(
      showModal({
        type: 'confirmation',
        message: t('Complete Review'),
        icon: <PiFlagCheckeredLight />,
        content: confirmMessage,
        showModal: true,
        onConfirm: async () => {
          dispatch(hideModal());
          await dispatch(completeReview(reviewUuid) as any);
        },
        onCancel: () => dispatch(hideModal()),
      }),
    );
  }, [dispatch, reviewUuid, currentSession, findings, buckets, t]);

  // ─── Reopen handler ────────────────────────────────────────────────────

  const handleReopenReview = useCallback(async () => {
    if (!reviewUuid) return;
    dispatch(
      showModal({
        type: 'confirmation',
        message: t('Reopen this review?'),
        content: t('All sign-offs will be cleared and collaborators will need to sign off again.'),
        icon: <PiArrowCounterClockwiseLight className='w-4 h-4' />,
        showModal: true,
        onConfirm: async () => {
          await dispatch(reopenReview(reviewUuid) as any);
          dispatch(hideModal());
        },
        onCancel: () => dispatch(hideModal()),
      }),
    );
  }, [dispatch, reviewUuid, t]);

  // ─── Download final handler ────────────────────────────────────────────

  const handleDownloadFinal = useCallback(async () => {
    if (!reviewUuid) return;
    await dispatch(downloadFinalDocuments(reviewUuid) as any);
  }, [dispatch, reviewUuid]);

  // ─── Delete handler ─────────────────────────────────────────────────────

  const handleDelete = useCallback(() => {
    if (!reviewUuid) return;
    dispatch(
      showModal({
        type: 'confirmation',
        message: t('Delete Review'),
        icon: <PiTrashLight className='text-danger-600 w-4 h-4' />,
        content: t('Are you sure you want to delete this review session? This action can be undone.'),
        showModal: true,
        onConfirm: async () => {
          const success = await dispatch(deleteReviewSession(reviewUuid) as any);
          if (success) {
            dispatch(hideModal());
            history.push('/reviews');
          }
        },
        onCancel: () => dispatch(hideModal()),
      }),
    );
  }, [dispatch, reviewUuid, history, t]);

  // ─── Help guide modal ──────────────────────────────────────────────────

  const handleOpenHelp = useCallback(() => {
    dispatch(showModal({
      showModal: true,
      type: 'reviewHelp' as any,
      message: t('How Document Review Works'),
      icon: <PiQuestionLight className="w-5 h-5 text-gray-500" />,
    }));
  }, [dispatch, t]);

  // ─── Header dropdown actions ────────────────────────────────────────────

  const isOwner = useMemo(() => {
    if (!userInfo?.UserUID || !currentSession?.collaborators) return false;
    return currentSession.collaborators.some(
      (c) => c.user_id === userInfo.UserUID && c.access === 'admin',
    );
  }, [userInfo?.UserUID, currentSession?.collaborators]);

  const headerDropdownActions = useMemo(() => {
    const actions: Array<{
      label: string;
      onClick: () => void;
      icon?: React.ReactElement;
      variant?: 'primary' | 'neutral' | 'outline' | 'destructive';
    }> = [
      /*
        FIRST, and no longer a lone `?` button in the header.

        A question mark next to the collaborators was a control with no
        noun - it read as help for the page chrome it sat in. In the menu it
        gets a name, and the header gets the space back for the readout.
      */
      {
        label: t('Info'),
        icon: <PiQuestionLight className="w-4 h-4" />,
        onClick: handleOpenHelp,
      },
      {
        label: t('Activity Log'),
        icon: <PiClockCounterClockwiseLight className="w-4 h-4" />,
        onClick: () => setShowAuditLog(true),
      },
      ...(currentSession?.source_table_uuid ? [{
        label: t('Back to Table'),
        icon: <PiArrowUpRightLight className="w-4 h-4" />,
        onClick: () => history.push(`/tables/${currentSession.source_table_uuid}`),
      }] : []),
      {
        label: t('Manage Access'),
        icon: <PiShareNetworkLight className="w-4 h-4" />,
        onClick: handleShare,
      },
    ];

    // Scan the currently-selected document against legislation. Only when a
    // specific (not "all") document is in view, since a scan runs on one doc.
    if (reviewUuid && selectedDocumentId !== 'all') {
      actions.push({
        label: t('Run legislation scan'),
        icon: <PiScalesLight className="w-4 h-4" />,
        onClick: () =>
          dispatch(
            showSidePanel({
              type: 'scan',
              showSidePanel: true,
              size: 'md',
              message: t('Legislation scan'),
              target: {
                kind: 'reviewDocument',
                sessionUuid: reviewUuid,
                documentId: selectedDocumentId as number,
              },
            })
          ),
      });
    }

    if (isFinalized && isOwner) {
      actions.push({
        label: t('Reopen Review'),
        icon: <PiArrowCounterClockwiseLight className="w-4 h-4" />,
        onClick: handleReopenReview,
      });
    }

    if (isOwner) {
      actions.push({
        label: t('Delete'),
        icon: <PiTrashLight className="w-4 h-4" />,
        onClick: handleDelete,
        variant: 'destructive',
      });
    }

    return actions;
  }, [t, handleOpenHelp, handleShare, handleReopenReview, handleDelete, currentSession?.source_table_uuid, isOwner, isFinalized, history, reviewUuid, selectedDocumentId, dispatch]);

  /**
   * Completing the review is the page's ONE primary action.
   *
   * It used to sit at the right end of the summary bar, which is where a
   * page's chrome goes, not where its decisions go. In the header it is
   * where every other detail page puts the thing you came to do - and it
   * still only appears once the analysis has finished.
   */
  const headerActions = useMemo(() => {
    const finishedProcessing =
      currentSession?.status === 'completed' || currentSession?.status === 'completed_with_errors';
    if (!finishedProcessing || isProcessing) return [];
    return [{
      label: t('Complete Review'),
      icon: <PiFlagCheckeredLight className="w-4 h-4" />,
      onClick: handleCompleteReview,
    }];
  }, [t, currentSession?.status, isProcessing, handleCompleteReview]);

  // ─── Breadcrumbs ───────────────────────────────────────────────────────

  const breadcrumbs = useMemo(() => {
    const sessionName = currentSession?.name || currentReview?.name || t('Review');

    return [
      { name: t('Reviews'), href: '/reviews', current: false },
      { name: sessionName, href: '', current: true },
    ];
  }, [currentSession?.name, currentReview, t]);
  // ─── Collaborators for header ──────────────────────────────────────

  const headerCollaborators = useMemo(() => {
    if (!currentSession?.collaborators) return [];
    // Through `accessModel` - session collaborator rows carry the picture as
    // `avatar_url` on some endpoints and `profile_picture` on others, and the
    // header stack only ever read the first spelling.
    return currentSession.collaborators.map((c) => {
      const person = normalise(c);
      return {
        user_uuid: c.user_id ?? person.id,
        name: person.name,
        initials: person.initials,
        access: c.access || person.access,
        email: person.email || null,
        avatar_url: person.avatar,
      };
    });
  }, [currentSession?.collaborators]);
  // ─── Stats ─────────────────────────────────────────────────────────────

  // ─── Document progress ────────────────────────────────────────────────

  const documentsCompleted = useMemo(() => {
    if (!currentSession?.documents) return 0;
    return currentSession.documents.filter(
      (d) => d.status === 'completed' || d.status === 'failed',
    ).length;
  }, [currentSession?.documents]);

  const documentsTotal = currentSession?.document_count || 0;
  const progressPercent = documentsTotal > 0 ? Math.round((documentsCompleted / documentsTotal) * 100) : 0;

  /*
    THE RUN, VISIBLE FROM t=0.

    The findings column was silent until the first finding streamed in - a
    skeleton, then a small loader in a fixed `h-32` box, in a column several
    hundred pixels tall. On a long document that is a minute or more of a
    screen that looks broken, at exactly the moment the user most needs to
    know something is happening.

    So the panel is driven by the RUN LIFECYCLE, not by the arrival of
    findings. `displayStages` stands in a `Connecting` stage whenever a run is
    live, so there is a labelled, pulsing stage on screen before the socket has
    delivered anything at all - and if it never delivers, that stage stays up
    saying "connecting" rather than the column going blank.

    Everything in here is MEASURED, not invented:

     - `percent` is the share of DOCUMENTS the session reports as finished. It
       is a real ratio of two real counts, not a clock-based guess - and it is
       null until at least one document is actually done, so the opening state
       is the sliver rather than a confident 0%.
     - the sub-items are the session's own documents with the statuses it
       reports for them. Review is one of the flows whose engine does report
       per-file progress, so the list earns its place here (unlike matter
       creation, which suppresses it).
     - the stage advances only on facts we hold: connecting until the socket is
       up, analysing once the session says it is processing.
  */
  /*
    THE CLOCK BELONGS TO THE RUN, NOT TO THE SESSION.

    Elapsed was `now − created_at`, and `created_at` is when the SESSION was
    created - not when the analysis running right now started. A review re-run
    (or simply opened) two hours after it was created therefore opened its
    progress panel already reading "120m" over work that was seconds old.

    The wire has nothing better on offer: `ReviewSessionDetail` carries
    `created_at`, `completed_at` and no field at all for when the current run
    began. So the start is OBSERVED here - the moment the session turns live is
    this run's t=0, and it resets on every later run. `created_at` stands in
    only when the session was ALREADY live at mount (the transition genuinely
    happened before we were watching) AND is recent enough to plausibly BE this
    run's start; past that window the clock starts from when we began watching,
    which understates by seconds rather than overstating by hours.
  */
  const sessionCreatedAt = useMemo(() => {
    const raw = (currentSession as any)?.created_at;
    const parsed = raw ? Date.parse(raw) : Number.NaN;
    return Number.isFinite(parsed) ? parsed : null;
  }, [currentSession]);

  const [sessionStartedAt, setSessionStartedAt] = useState<number | null>(null);

  useEffect(() => {
    if (!isProcessing) {
      // Not live: the next run gets a fresh t=0 rather than inheriting this one.
      setSessionStartedAt(null);
      return;
    }
    setSessionStartedAt((prev) => {
      if (prev !== null) return prev;
      const now = Date.now();
      const age = sessionCreatedAt === null ? null : now - sessionCreatedAt;
      return age !== null && age >= 0 && age <= RUN_START_FALLBACK_WINDOW_MS
        ? sessionCreatedAt
        : now;
    });
  }, [isProcessing, sessionCreatedAt]);

  const reviewRun: AIRun = useMemo(() => {
    const docs = currentSession?.documents || [];
    const anyDone = documentsCompleted > 0;
    return {
      ...initialAIRun,
      /* `pending` is the server saying "queued, not started" - that is the
         connecting state, and with no stages `displayStages` stands in its
         own Connecting stage. `processing` is the server saying work has
         begun, which is the one stage we can name from fact. */
      status: currentSession?.status === 'processing' ? 'running'
        : currentSession?.status === 'pending' ? 'connecting'
        /* A finished session must reach a TERMINAL run status, not `idle`:
           `useAIRunElapsed` only stops its 1s interval on a terminal one, so
           `idle` left a timer ticking for the life of the page. */
        : currentSession?.status === 'failed' ? 'failed'
        : currentSession?.status ? 'completed'
        : 'idle',
      stages: currentSession?.status === 'processing'
        ? [{ key: 'analysing_documents', label: t('Analysing documents'), status: 'active' as const, startedAt: 0 }]
        : [],
      subItems: docs.map((doc: any) => ({
        id: String(doc.uuid || doc.id || doc.file_name),
        name: doc.file_name || t('Document'),
        status: doc.status === 'completed' ? ('completed' as const)
          : doc.status === 'failed' ? ('failed' as const)
          : doc.status === 'processing' ? ('active' as const)
          : ('pending' as const),
        percent: null,
      })),
      percent: anyDone && documentsTotal > 0 ? progressPercent : null,
      stepCurrent: anyDone ? documentsCompleted : 0,
      stepTotal: documentsTotal,
      startedAt: sessionStartedAt ?? null,
    };
  }, [currentSession?.documents, currentSession?.status, documentsCompleted, documentsTotal, progressPercent, sessionStartedAt, t]);
  const reviewElapsed = useAIRunElapsed(reviewRun);

  /*
    THE DECISIONS, AS A CACHE KEY.

    The revised document is rendered by the SERVER from the accepted findings,
    so it is stale the moment one is accepted, rejected or undone - and nothing
    the viewer already keyed on (`sessionUuid`, `documentId`, `revisionStyle`)
    moves when that happens. So it kept showing pre-decision text until the
    reviewer reloaded the page by hand, once per finding.

    Derived from the findings rather than bumped by the decision handler on
    purpose: it is then equally right for the bulk accept/reject path, for undo,
    for a `modified_text` edit, and for a decision another reviewer makes that
    arrives over pubsub - none of which go through `handleDecision`.

    `decision` AND `modified_text`, because modifying an already-accepted
    finding changes the document without changing the decision.
  */
  const revisionToken = useMemo(
    () => findings.map((f) => `${f.uuid}:${f.decision ?? ''}:${(f as any).modified_text ? '1' : '0'}`).join('|'),
    [findings],
  );

  /*
    Which dock the findings are in - drives the flip control's direction.

    There is no longer a second, hand-rolled findings column for reading
    mode: expanded and docked are the same tree now, so the pane IS the
    findings in both, with its own resize handle, close and flip. One
    implementation, one width, one set of behaviours.
  */
  const findingsDock = panels.layout.slots.findings.dock;


  // ─── Loading ───────────────────────────────────────────────────────────

  if (isLoadingSession && !currentSession) {
    return (
      <DetailFrame breadcrumbs={breadcrumbs} actions={[]}>
        <Skeleton variant="detail" />
      </DetailFrame>
    );
  }

  /*
    ── The findings pane's body ────────────────────────────────────────

    Hoisted out of the panes array because it has TWO homes now. Docked, it
    is the left pane. Expanded, the surface is not rendered at all - the
    document is the only child of the frame - and the findings have to come
    back as an overlay over it, or expanding a document to read it properly
    would be the one state in which you cannot act on what you are reading.

    Same element in both, so the list, the finding detail, the decision
    buttons and every piece of state behind them are identical: there is no
    second implementation to drift.
  */

  const findingsPaneBody = (
            <div
              className="flex flex-col h-full min-h-0 overflow-hidden"
              data-tour-id="review-findings-panel"
            >
              {selectedFinding ? (
                /*
                  ── The finding being read ──────────────────────────────

                  THE ONLY PLACE a finding is read or decided. It used to
                  render in the centre of the page while the list sat here,
                  so one finding was on screen twice and the document - the
                  thing under review - had nowhere to go.

                  Master-detail, like the side panel it descends from: the
                  list becomes the finding, and Back returns to the list.
                */
                <div
                  className="flex flex-col h-full min-h-0 overflow-hidden"
                  data-testid="review-finding-detail"
                  data-tour-id="review-finding-detail"
                >
                  <div className="flex-shrink-0 px-3 py-2 border-b border-hairline-soft">
                    <button
                      type="button"
                      data-testid="finding-detail-back"
                      onClick={() => setSelectedFindingUuid(null)}
                      className="inline-flex items-center gap-1 text-xs font-medium text-fg-muted
                                 hover:text-fg-body transition-colors duration-200 ease-slide"
                    >
                      <PiCaretLeftLight className="w-3.5 h-3.5 rtl:-scale-x-100" />
                      {t('Back to findings')}
                    </button>
                  </div>
                    {/*
                      ── The finding ─────────────────────────────────────

                      One loop, top to bottom: read the finding, weigh the risk,
                      see the change, decide. Everything here serves that order.

                      What it is NOT, having been both: a stack of glass cards (one
                      per field - exhausting), and a bare label-then-prose dump (no
                      hierarchy, and a thin column adrift in a wide canvas). The
                      settled answer boxes exactly ONE thing - the suggested change
                      - because that is the thing the buttons act on. Identity and
                      metrics are structured with type and alignment instead, and
                      the reasoning is plain prose.

                      The record (notes, history) moves off the main scroll into
                      tabs, so the reasoning is not something you scroll past.
                    */}
                    <div className="flex-1 overflow-y-auto custom-scrollbar-main min-h-0">
                    <div
                      className="w-full px-4 pt-4 pb-8 min-h-full flex flex-col"
                      data-tour-id="review-finding-header"
                    >
                      {/* ── Header ──────────────────────────────────────── */}
                      <header>
                        <div className="flex items-center justify-between gap-4">
                          {/* Severity, as a labelled state rather than a badge —
                              it is a property of the finding, not a chip on it. */}
                          <span className={clsx(
                            'inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider',
                            selectedFinding.severity === 'critical' ? 'text-danger-600 dark:text-danger-400' :
                            selectedFinding.severity === 'major' ? 'text-warning-600 dark:text-warning-400' :
                            selectedFinding.severity === 'minor' ? 'text-warning-700 dark:text-warning-400' :
                            'text-info-600 dark:text-info-400',
                          )}>
                            <span className={clsx(
                              'w-1.5 h-1.5 rounded-full',
                              selectedFinding.severity === 'critical' ? 'bg-danger-500' :
                              selectedFinding.severity === 'major' ? 'bg-warning-400' :
                              selectedFinding.severity === 'minor' ? 'bg-warning-400' : 'bg-info-400',
                            )} aria-hidden />
                            {severityLabels[selectedFinding.severity]}
                          </span>

                          {/* The decision, or the fact that it is still yours to make. */}
                          {selectedFinding.decision ? (
                            <span className={clsx(
                              'inline-flex items-center gap-1 text-[11px] font-medium',
                              selectedFinding.decision === 'accepted' ? 'text-success-600 dark:text-success-400' :
                              selectedFinding.decision === 'rejected' ? 'text-danger-600 dark:text-danger-400' :
                              selectedFinding.decision === 'modified' ? 'text-warning-600 dark:text-warning-400' :
                              selectedFinding.decision === 'escalated' ? 'text-purple-600 dark:text-purple-400' : 'text-fg-muted',
                            )}>
                              {selectedFinding.decision === 'accepted' && <PiCheckCircleLight className="w-3.5 h-3.5" />}
                              {selectedFinding.decision === 'rejected' && <PiXCircleLight className="w-3.5 h-3.5" />}
                              {selectedFinding.decision === 'modified' && <PiPencilSimpleLight className="w-3.5 h-3.5" />}
                              {selectedFinding.decision === 'escalated' && <PiArrowUpRightLight className="w-3.5 h-3.5" />}
                              {formatLabel(selectedFinding.decision)}
                            </span>
                          ) : decisionsLocked ? (
                            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-fg-muted">
                              <PiLockKeyLight className="w-3.5 h-3.5" />
                              {t('Locked')}
                            </span>
                          ) : !isProcessing ? (
                            <span className="text-[11px] font-medium text-fg-muted">
                              {t('Awaiting your decision')}
                            </span>
                          ) : null}
                        </div>

                        <h1 className="mt-3 text-[19px] font-[650] tracking-tight text-fg-strong leading-[1.25]">
                          {formatLabel(selectedFinding.clause_type)}
                        </h1>

                        {/*
                          Identity LEFT, metrics RIGHT, both sitting on the hairline
                          that closes the header.

                          The four facts were a faint inline string once and four
                          glass tiles after that - one unreadable, the other
                          cluttered. Aligned label-over-value pairs give the numbers
                          their own weight without adding a single container.
                        */}
                        <div className="mt-4 pb-3.5 border-b border-hairline
                                        flex items-end justify-between gap-6">
                          <div className="min-w-0 flex items-center gap-1.5 text-[12px]">
                            {selectedFinding.document_name && (
                              <button
                                type="button"
                                /*
                                  Opens the viewer SCROLLED TO THIS FINDING.

                                  There used to be a separate "View in document"
                                  link carrying the scroll target while the document
                                  name did a plain open - two controls onto the same
                                  document, one of them worse. The name now carries
                                  the deep link, so the header keeps one affordance
                                  and loses no capability.
                                */
                                onClick={() => !isProcessing && showDocument(
                                  selectedFinding.document_id,
                                  selectedFinding.uuid,
                                  selectedFinding.original_text || selectedFinding.issue_description || '',
                                  selectedFinding.section_reference || '',
                                )}
                                disabled={isProcessing}
                                title={t('Open in document viewer') as string}
                                className={clsx(
                                  'truncate font-medium transition-colors duration-200 ease-slide',
                                  isProcessing ? 'text-fg-muted cursor-not-allowed' : 'text-champagne hover:underline',
                                )}
                              >
                                {selectedFinding.document_name}
                              </button>
                            )}
                            {selectedFinding.document_name && selectedFinding.section_reference && (
                              <span className="text-fg-muted flex-shrink-0" aria-hidden>·</span>
                            )}
                            {selectedFinding.section_reference && (
                              <span className="truncate text-fg-muted">{selectedFinding.section_reference}</span>
                            )}
                          </div>

                          {(selectedFinding.risk_score !== null || selectedFinding.confidence_score !== null) && (
                            <div className="flex items-end gap-4 flex-shrink-0" data-testid="finding-metrics">
                              {selectedFinding.risk_score !== null && (
                                <div className="text-end">
                                  <p className={METRIC_LABEL}>{t('Risk')}</p>
                                  <p className={clsx(
                                    'mt-1.5 text-[17px] font-semibold tabular-nums leading-none',
                                    (selectedFinding.risk_score ?? 0) >= 7 ? 'text-danger-600 dark:text-danger-400' :
                                    (selectedFinding.risk_score ?? 0) >= 4 ? 'text-warning-600 dark:text-warning-400' :
                                    'text-success-600 dark:text-success-400',
                                  )}>
                                    {selectedFinding.risk_score}
                                  </p>
                                </div>
                              )}
                              {selectedFinding.risk_score !== null && selectedFinding.confidence_score !== null && (
                                <div className="self-stretch border-s border-hairline" aria-hidden />
                              )}
                              {selectedFinding.confidence_score !== null && (
                                /*
                                  A BAND, not a percentage.

                                  "87%" invited arithmetic the number cannot carry:
                                  nobody calibrated it, and 84 beside 87 is not a
                                  distinction a reviewer can act on. High / Medium /
                                  Low is the whole of what it says. Risk stays
                                  numeric one column left - that IS scored on a
                                  scale the playbook defines.
                                */
                                <div className="text-end" data-testid="finding-confidence">
                                  <p className={METRIC_LABEL}>{t('Confidence')}</p>
                                  {(() => {
                                    const band = confidenceBand(selectedFinding.confidence_score);
                                    return (
                                      <p
                                        data-band={band ?? 'none'}
                                        className={clsx(
                                          'mt-1.5 text-[17px] font-semibold leading-none',
                                          band ? CONFIDENCE_TONE[band] : 'text-fg-muted',
                                        )}
                                      >
                                        {band ? t(CONFIDENCE_LABEL[band]) : CONFIDENCE_EMPTY}
                                      </p>
                                    );
                                  })()}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </header>

                      {/*
                        ── Tabs ────────────────────────────────────────────

                        The reasoning is the work; the notes thread and the history
                        are the record of it. They used to sit below the reasoning
                        on one scroll, so the change block - the thing being decided
                        - drifted further from the buttons the more notes a finding
                        collected. Tabs keep the decision loop a fixed length.
                      */}
                      <div
                        role="tablist"
                        aria-label={t('Finding sections')}
                        data-testid="finding-tabs"
                        className="mt-5 inline-flex items-center gap-0.5 p-0.5 rounded-full
                                   bg-surface-sunken border border-hairline-soft"
                      >
                        {([
                          { id: 'analysis' as const, label: t('Analysis') },
                          { id: 'notes' as const, label: t('Notes'), count: selectedFinding.notes?.length || 0 },
                          { id: 'activity' as const, label: t('Activity') },
                        ]).map((tab) => {
                          const active = findingTab === tab.id;
                          return (
                            <button
                              key={tab.id}
                              type="button"
                              role="tab"
                              aria-selected={active}
                              data-testid={`finding-tab-${tab.id}`}
                              data-active={active}
                              onClick={() => setFindingTab(tab.id)}
                              className={clsx(
                                'nav-pill inline-flex items-center gap-1.5 rounded-full px-3 py-1',
                                'text-[12px] font-medium transition-colors duration-200 ease-slide',
                                active ? 'nav-pill-active text-fg-strong' : 'text-fg-muted hover:text-fg-body',
                              )}
                            >
                              {tab.label}
                              {!!tab.count && (
                                <span className={clsx(
                                  'min-w-[16px] px-1 rounded-full text-[10px] font-semibold tabular-nums leading-4',
                                  active ? 'bg-champagne text-ink' : 'bg-surface text-fg-muted',
                                )}>
                                  {tab.count}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>

                      {/* ── Analysis ───────────────────────────────────── */}
                      {findingTab === 'analysis' && (
                        <div data-testid="finding-analysis">
                          {selectedFinding.issue_description && (
                            <section data-testid="finding-issue">
                              <h2 className={SECTION_LABEL}>{t('Issue')}</h2>
                              <p className="max-w-[64ch] text-[14px] leading-7 text-fg-body">
                                {withReferences(selectedFinding.issue_description)}
                              </p>
                            </section>
                          )}

                          {selectedFinding.legal_basis && (
                            <section data-testid="finding-basis">
                              <h2 className={SECTION_LABEL}>{t('Legal basis')}</h2>
                              <p className="max-w-[64ch] text-[14px] leading-7 text-fg-muted">
                                {withReferences(
                                  typeof selectedFinding.legal_basis === 'string'
                                    ? selectedFinding.legal_basis
                                    : JSON.stringify(selectedFinding.legal_basis, null, 2)
                                )}
                              </p>
                            </section>
                          )}

                          {/*
                            ── The change - the ONE container ──────────────

                            Last in the column and directly above the buttons,
                            because it is what Accept / Reject / Modify act on.
                            Context first: you have read why before you are shown
                            what to do about it.
                          */}
                          {(selectedFinding.suggested_redline || showModifyInput || selectedFinding.original_text) && (
                            <section className="mt-8" data-testid="finding-change">
                              <GlassCard
                                title={t('Suggested change')}
                                actions={
                                  <span className="text-[11px] text-fg-muted truncate max-w-[220px]">
                                    {selectedFinding.action_type === 'add_after' || selectedFinding.action_type === 'add_before'
                                      ? [
                                          t('Addition'),
                                          selectedFinding.section_reference
                                            ? `${selectedFinding.action_type === 'add_after' ? t('after') : t('before')} ${selectedFinding.section_reference}`
                                            : null,
                                        ].filter(Boolean).join(' · ')
                                      : t('Replacement')}
                                  </span>
                                }
                              >
                                {(() => {
                                  // A pure addition has nothing to compare against;
                                  // an empty "Current" pane beside it would be a
                                  // container drawn to hold nothing.
                                  const isAddition =
                                    selectedFinding.action_type === 'add_after' ||
                                    selectedFinding.action_type === 'add_before';
                                  const showCurrent = !!selectedFinding.original_text && !isAddition;

                                  const suggested = (
                                    <div className="flex flex-col min-w-0" data-testid="change-suggested">
                                      <p className={PAIR_LABEL}>
                                        {showModifyInput ? t('Your modification') : t('Suggested')}
                                      </p>
                                      {showModifyInput ? (
                                        <Textarea
                                          id="modified-redline"
                                          label=""
                                          value={modifiedText}
                                          onChange={(e) => setModifiedText(e.target.value)}
                                          rows={8}
                                        />
                                      ) : (
                                        <Well className="flex-1 !bg-success-50/50 dark:!bg-success-900/20
                                                         !border-success-200 dark:!border-success-800/50">
                                          <p className="text-[13px] leading-6 text-fg-body whitespace-pre-wrap">
                                            {selectedFinding.suggested_redline}
                                          </p>
                                        </Well>
                                      )}
                                    </div>
                                  );

                                  if (!showCurrent) return suggested;

                                  return (
                                    <div className="grid gap-3 items-stretch md:grid-cols-[1fr_auto_1fr]">
                                      <div className="flex flex-col min-w-0" data-testid="change-current">
                                        <p className={PAIR_LABEL}>{t('Current')}</p>
                                        {/* Struck through: this is the text going away. */}
                                        <Well className="flex-1">
                                          <p className="text-[13px] leading-6 text-fg-muted line-through
                                                        decoration-fg-muted/40 whitespace-pre-wrap">
                                            {selectedFinding.original_text}
                                          </p>
                                        </Well>
                                      </div>

                                      <div className="hidden md:flex items-center justify-center px-1">
                                        <PiArrowRightLight className="w-4 h-4 text-fg-muted rtl:-scale-x-100" aria-hidden />
                                      </div>

                                      {suggested}
                                    </div>
                                  );
                                })()}
                              </GlassCard>

                              {/* What was actually applied, once it has been. */}
                              {selectedFinding.decision === 'modified' && selectedFinding.modified_text && (
                                <div className="mt-4" data-testid="finding-modification">
                                  <h2 className={SECTION_LABEL}>{t('Applied modification')}</h2>
                                  <Well>
                                    {selectedFinding.original_text ? (
                                      <InlineDiff
                                        original={selectedFinding.original_text}
                                        modified={selectedFinding.modified_text}
                                      />
                                    ) : (
                                      <p className="text-[13px] leading-6 text-fg-body whitespace-pre-wrap">
                                        {selectedFinding.modified_text}
                                      </p>
                                    )}
                                  </Well>
                                  {selectedFinding.applied_at && (
                                    <p className="mt-1.5 text-[11px] text-fg-muted">
                                      {t('Applied')} {formatUtcToLocal(selectedFinding.applied_at)}
                                      {selectedFinding.decision_by_name && ` ${t('by')} ${selectedFinding.decision_by_name}`}
                                    </p>
                                  )}
                                </div>
                              )}
                            </section>
                          )}
                        </div>
                      )}

                      {/* ── Notes ──────────────────────────────────────── */}
                      {/*
                        ── Notes ───────────────────────────────────────────

                        A thread, laid out like one: the list takes the height that
                        is left and scrolls inside itself, and the composer is
                        PINNED beneath it. Previously the composer sat after the
                        list in one long scroll, so the more notes a finding had,
                        the further you had to scroll to add another - the control
                        receded exactly as the thread got busy.
                      */}
                      {findingTab === 'notes' && (
                        <div className="mt-6 flex-1 min-h-0 flex flex-col" data-testid="finding-notes">
                          {isProcessing ? (
                            <p className="py-8 text-center text-[13px] text-fg-muted">
                              {t('Notes become available once processing completes.')}
                            </p>
                          ) : (
                            <>
                              <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main -mx-1 px-1">
                                {selectedFinding.notes && selectedFinding.notes.length > 0 ? (
                                  <ul className="divide-y divide-hairline-soft border-b border-hairline-soft">
                                    {selectedFinding.notes.map((note) => {
                                      const isEditing = editingNoteUuid === note.uuid;
                                      const isOwnNote = !!note.is_mine;
                                      const author = authorIdentity(note.author_name, note.is_mine);
                                      return (
                                        <li
                                          key={note.uuid}
                                          id={`note-${note.uuid}`}
                                          data-highlighted={highlightedNote === note.uuid}
                                          className={clsx(
                                            'group flex items-start gap-3 py-3.5 px-2 -mx-2 rounded-lg',
                                            'transition-colors duration-500 ease-slide',
                                            // Fades out on its own: this marks where
                                            // you landed, it is not a selection.
                                            highlightedNote === note.uuid && 'bg-champagne/10'
                                          )}
                                        >
                                          <ProfileImage
                                            profilePicture={normalise(author).avatar}
                                            email={author.email}
                                            initials={initialsFor(author.name || note.author_name)}
                                            name={author.name || note.author_name || undefined}
                                            size="h-7 w-7"
                                            className="mt-0.5 !cursor-default"
                                          />
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between gap-2 mb-1">
                                              <div className="flex items-center gap-1.5 text-[12px] min-w-0">
                                                <span className="font-medium text-fg-body truncate">
                                                  {note.author_name || t('Unknown')}
                                                </span>
                                                {note.created_at && (
                                                  <span className="text-fg-muted flex-shrink-0">
                                                    · {formatUtcToLocal(note.created_at)}
                                                  </span>
                                                )}
                                                {note.updated_at && note.created_at && note.updated_at !== note.created_at && (
                                                  <span className="text-fg-muted flex-shrink-0">· {t('(edited)')}</span>
                                                )}
                                              </div>
                                              {!isEditing && isOwnNote && (
                                                <div className="flex items-center gap-0.5 flex-shrink-0
                                                                opacity-0 group-hover:opacity-100 focus-within:opacity-100
                                                                transition-opacity duration-200 ease-slide">
                                                  <button
                                                    onClick={() => {
                                                      setEditingNoteUuid(note.uuid);
                                                      setEditingContent(note.content || '');
                                                    }}
                                                    className="p-1 rounded-md text-fg-muted hover:text-fg-strong
                                                               transition-colors duration-200 ease-slide"
                                                    title={t('Edit')}
                                                  >
                                                    <PiPencilSimpleLight className="w-3.5 h-3.5" />
                                                  </button>
                                                  <button
                                                    onClick={() => handleDeleteNote(note.uuid)}
                                                    disabled={isSavingNotes}
                                                    className="p-1 rounded-md text-fg-muted hover:text-danger-500
                                                               transition-colors duration-200 ease-slide disabled:opacity-50"
                                                    title={t('Delete')}
                                                  >
                                                    <PiTrashLight className="w-3.5 h-3.5" />
                                                  </button>
                                                </div>
                                              )}
                                            </div>
                                            {isEditing ? (
                                              <div>
                                                <Textarea
                                                  id={`note-edit-${note.uuid}`}
                                                  label=""
                                                  value={editingContent}
                                                  onChange={(e) => setEditingContent(e.target.value)}
                                                  rows={3}
                                                />
                                                <div className="flex justify-end gap-2 mt-2">
                                                  <Button
                                                    variant="neutral"
                                                    size="small"
                                                    onClick={() => {
                                                      setEditingNoteUuid(null);
                                                      setEditingContent('');
                                                    }}
                                                    disabled={isSavingNotes}
                                                  >
                                                    {t('Cancel')}
                                                  </Button>
                                                  <Button
                                                    variant="primary"
                                                    size="small"
                                                    onClick={async () => {
                                                      const ok = await handleUpdateNote(note.uuid, editingContent);
                                                      if (ok) {
                                                        setEditingNoteUuid(null);
                                                        setEditingContent('');
                                                      }
                                                    }}
                                                    disabled={
                                                      isSavingNotes ||
                                                      !editingContent.trim() ||
                                                      editingContent.trim() === (note.content || '').trim()
                                                    }
                                                  >
                                                    {isSavingNotes ? t('Saving...') : t('Update')}
                                                  </Button>
                                                </div>
                                              </div>
                                            ) : (
                                              <p className="text-[13px] leading-6 text-fg-body whitespace-pre-wrap">
                                                {note.content}
                                              </p>
                                            )}
                                          </div>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                ) : (
                                  <p className="py-10 text-center text-[13px] text-fg-muted">
                                    {t('No notes yet. Add one to record your reasoning.')}
                                  </p>
                                )}
                              </div>

                              {/* Pinned beneath the thread, at the same distance
                                  from it however long it gets. */}
                              <div className="flex-shrink-0 pt-3">
                                <Textarea
                                  id="new-note"
                                  label=""
                                  placeholder={t('Add a note...')}
                                  value={decisionNotes}
                                  onChange={(e) => setDecisionNotes(e.target.value)}
                                  rows={3}
                                />
                                <div className="flex justify-end mt-2">
                                  <Button
                                    variant="primary"
                                    size="small"
                                    icon={<PiPlusCircleLight className="w-4 h-4" />}
                                    onClick={handleAddNote}
                                    disabled={isSavingNotes || !decisionNotes.trim()}
                                  >
                                    {isSavingNotes ? t('Saving...') : t('Add note')}
                                  </Button>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      )}

                      {/* ── Activity ───────────────────────────────────── */}
                      {findingTab === 'activity' && (
                        <div className="mt-6 flex-1 min-h-0 overflow-y-auto custom-scrollbar-main"
                             data-testid="finding-activity">
                          {findingActivity.length === 0 ? (
                            <p className="py-10 text-center text-[13px] text-fg-muted">
                              {t('No activity yet')}
                            </p>
                          ) : (
                            <ul className="space-y-0">
                              {findingActivity.map((event, i) => {
                                const actor = authorIdentity(event.who, event.isMine);
                                return (
                                  <li key={event.key} className="flex items-start gap-3">
                                    {/* The author IS the node on the rail: whose
                                        action this was is the first thing you want
                                        from a history. */}
                                    <div className="flex flex-col items-center self-stretch flex-shrink-0 pt-2.5">
                                      <ProfileImage
                                        profilePicture={normalise(actor).avatar}
                                        email={actor.email}
                                        initials={initialsFor(actor.name || event.who)}
                                        name={actor.name || event.who || undefined}
                                        size="h-6 w-6"
                                        className="!cursor-default"
                                      />
                                      {i < findingActivity.length - 1 && (
                                        <span className="flex-1 w-px bg-hairline-soft mt-1.5" aria-hidden />
                                      )}
                                    </div>

                                    {/*
                                      An entry is a LINK to what it describes: a
                                      note entry opens the Notes tab with that note
                                      marked, a decision or modification opens the
                                      Analysis tab where the change is. It goes
                                      through the same state the URL reflects, so
                                      the jump is in the address bar and shareable.
                                    */}
                                    <button
                                      type="button"
                                      data-testid="activity-entry"
                                      data-target={event.target}
                                      onClick={() => goToActivity(event, selectedFinding.uuid)}
                                      className="group min-w-0 flex-1 text-start py-2.5 px-2 -mx-2 rounded-lg
                                                 hover:bg-surface-sunken/60
                                                 transition-colors duration-200 ease-slide"
                                    >
                                      <p className="text-[13px] text-fg-body flex items-center gap-2">
                                        <span className="truncate group-hover:text-fg-strong
                                                         transition-colors duration-200 ease-slide">
                                          {t(event.label)}
                                        </span>
                                        {/* The kind is carried by a dot, so the two
                                            record-keeping events stay tellable
                                            apart at a glance. */}
                                        <span className={clsx(
                                          'inline-block w-1.5 h-1.5 rounded-full flex-shrink-0',
                                          event.kind === 'decision' ? 'bg-champagne' :
                                          event.kind === 'applied' ? 'bg-success-400' : 'bg-fg-muted/40',
                                        )} aria-hidden />
                                        <PiArrowRightLight
                                          className="w-3 h-3 flex-shrink-0 text-fg-muted ms-auto
                                                     opacity-0 group-hover:opacity-100
                                                     transition-opacity duration-200 ease-slide rtl:-scale-x-100"
                                          aria-hidden
                                        />
                                      </p>
                                      <p className="text-[11px] text-fg-muted mt-0.5">
                                        {event.who && <>{event.who} · </>}
                                        {formatUtcToLocal(event.at)}
                                      </p>
                                    </button>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </div>
                      )}
                    </div>
                    </div>

                    {/*
                      ── The decision bar ──────────────────────────────

                      Pinned, and the only thing that is: whatever you have scrolled
                      to, the decision is one move away.

                      A gradient fade rather than a hard rule - the column should
                      dissolve into the actions, not be cut off by them. The buttons
                      sit in the SAME 720px column as the change block above, so
                      they line up under the thing they act on.
                    */}
                    <div className="flex-shrink-0 px-4 pt-4 pb-3 border-t border-hairline-soft
                                    bg-gradient-to-t from-surface via-surface/95 to-transparent">
                      <div className="w-full">
                      {isProcessing ? (
                        <div className="flex items-center gap-2 text-xs text-info-500 dark:text-info-400">
                          <BrandLoader size="xs" className="!h-4 !w-4" />
                          <span>{t('Analysis in progress — decisions available once processing completes')}</span>
                        </div>
                      ) : decisionsLocked ? (
                        <div className="flex items-center gap-2 text-xs text-gray-400">
                          <PiLockKeyLight className="w-4 h-4" />
                          <span>{t('Decisions are locked — this review has been finalized')}</span>
                        </div>
                      ) : selectedFinding.decision === 'escalated' ? (
                        /* Escalated - still fully actionable + option to remove escalation */
                        <div className="flex flex-wrap items-center gap-2" data-tour-id="review-finding-decisions">
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                            <PiArrowUpRightLight className="w-3.5 h-3.5" />
                            {t('Escalated')}
                          </span>
                          {selectedFinding.decision_by_name && (
                            <span className="text-xs text-gray-400">
                              {t('by')} {selectedFinding.decision_by_name}
                              {selectedFinding.decision_at && (
                                <span className="ms-1">· {formatUtcToLocal(selectedFinding.decision_at)}</span>
                              )}
                            </span>
                          )}
                          <div className="flex-1" />
                          <Button
                            variant="primary"
                            size="small"
                            icon={<PiCheckCircleLight className="w-4 h-4" />}
                            onClick={() => handleDecision('accepted')}
                          >
                            {t('Accept')}
                          </Button>
                          <Button
                            variant="destructive"
                            size="small"
                            icon={<PiXCircleLight className="w-4 h-4" />}
                            onClick={() => handleDecision('rejected')}
                          >
                            {t('Reject')}
                          </Button>
                          <Button
                            variant={showModifyInput ? 'neutral' : 'outline'}
                            size="small"
                            icon={showModifyInput
                              ? <PiXLight className="w-4 h-4" />
                              : <PiPencilSimpleLight className="w-4 h-4" />}
                            onClick={() => {
                              if (showModifyInput) {
                                setShowModifyInput(false);
                                setModifiedText('');
                              } else {
                                setShowModifyInput(true);
                                if (!modifiedText && selectedFinding.suggested_redline) {
                                  setModifiedText(selectedFinding.suggested_redline);
                                }
                              }
                            }}
                          >
                            {showModifyInput ? t('Cancel') : t('Modify')}
                          </Button>
                          {showModifyInput && (
                            <Button
                              variant="primary"
                              size="small"
                              icon={<PiCheckCircleLight className="w-4 h-4" />}
                              onClick={() => handleDecision('modified')}
                              disabled={!modifiedText.trim()}
                            >
                              {t('Submit modification')}
                            </Button>
                          )}
                          <Button
                            onClick={handleUndoDecision}
                            variant="neutral"
                            size="small"
                            icon={<PiArrowCounterClockwiseLight className="w-3.5 h-3.5" />}
                          >
                            {t('Remove escalation')}
                          </Button>
                        </div>
                      ) : selectedFinding.decision ? (
                        <div className="flex items-center gap-2.5">
                          <span className={clsx(
                            'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium',
                            selectedFinding.decision === 'accepted' ? 'bg-success-50 text-success-700 dark:bg-success-900/30 dark:text-success-400' :
                            selectedFinding.decision === 'rejected' ? 'bg-danger-50 text-danger-700 dark:bg-danger-900/30 dark:text-danger-400' :
                            selectedFinding.decision === 'modified' ? 'bg-warning-50 text-warning-700 dark:bg-warning-900/30 dark:text-warning-400' :
                            'bg-surface-sunken text-fg-muted',
                          )}>
                            {selectedFinding.decision === 'accepted' && <PiCheckCircleLight className="w-3.5 h-3.5" />}
                            {selectedFinding.decision === 'rejected' && <PiXCircleLight className="w-3.5 h-3.5" />}
                            {selectedFinding.decision === 'modified' && <PiPencilSimpleLight className="w-3.5 h-3.5" />}
                            {formatLabel(selectedFinding.decision)}
                          </span>
                          {selectedFinding.decision_by_name && (
                            <span className="text-xs text-gray-400">
                              {t('by')} {selectedFinding.decision_by_name}
                              {selectedFinding.decision_at && (
                                <span className="ms-1">· {formatUtcToLocal(selectedFinding.decision_at)}</span>
                              )}
                            </span>
                          )}
                          <div className="flex-1" />
                          <Button
                            onClick={handleUndoDecision}
                            variant="neutral"
                            size="small"
                            icon={<PiArrowCounterClockwiseLight className="w-3.5 h-3.5" />}
                          >
                            {t('Undo')}
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2" data-tour-id="review-finding-decisions">
                          <Button
                            variant="primary"
                            size="small"
                            icon={<PiCheckCircleLight className="w-4 h-4" />}
                            onClick={() => handleDecision('accepted')}
                          >
                            {t('Accept')}
                          </Button>
                          <Button
                            variant="destructive"
                            size="small"
                            icon={<PiXCircleLight className="w-4 h-4" />}
                            onClick={() => handleDecision('rejected')}
                          >
                            {t('Reject')}
                          </Button>
                          <Button
                            variant={showModifyInput ? 'neutral' : 'outline'}
                            size="small"
                            icon={showModifyInput
                              ? <PiXLight className="w-4 h-4" />
                              : <PiPencilSimpleLight className="w-4 h-4" />}
                            onClick={() => {
                              if (showModifyInput) {
                                setShowModifyInput(false);
                                setModifiedText('');
                              } else {
                                setShowModifyInput(true);
                                if (!modifiedText && selectedFinding.suggested_redline) {
                                  setModifiedText(selectedFinding.suggested_redline);
                                }
                              }
                            }}
                          >
                            {showModifyInput ? t('Cancel') : t('Modify')}
                          </Button>
                          {showModifyInput && (
                            <Button
                              variant="primary"
                              size="small"
                              icon={<PiCheckCircleLight className="w-4 h-4" />}
                              onClick={() => handleDecision('modified')}
                              disabled={!modifiedText.trim()}
                            >
                              {t('Submit modification')}
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="small"
                            icon={<PiArrowUpRightLight className="w-4 h-4" />}
                            onClick={() => handleDecision('escalated')}
                          >
                            {t('Escalate')}
                          </Button>
                        </div>
                      )}
                      </div>
                    </div>
                </div>
              ) : (
              <>
        {/* ── Filter bar ──────────────────────────────────── */}
        <div className="flex-shrink-0 px-2.5 py-1.5 flex items-center gap-1.5 border-b border-hairline-soft" data-tour-id="review-findings-filter-bar">
          {/* Severity dropdown */}
          <Menu as="div">
            <Menu.Button className="flex items-center gap-1 px-2 py-1 border border-hairline rounded text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-surface-sunken transition-colors">
              <PiFunnelLight className="w-3.5 h-3.5 text-gray-400" />
              <span>{severityFilter === 'all' ? t('Severity') : severityLabels[severityFilter as FindingSeverity]}</span>
              <PiCaretDownLight className="w-3 h-3 text-gray-400" />
            </Menu.Button>
            <Menu.Items
              anchor="bottom start"
              transition
              className="z-[300] [--anchor-gap:4px] w-44 bg-surface rounded-xl shadow-xl ring-1 ring-black/[0.05] dark:ring-white/[0.08] py-1 origin-top-left transition duration-150 ease-out data-[closed]:opacity-0 data-[closed]:scale-95"
            >
              <Menu.Item>
                {({ active }: { active: boolean }) => (
                  <button
                    onClick={() => setSeverityFilter('all')}
                    className={clsx(
                      'w-full text-start px-3 py-1.5 text-xs flex items-center justify-between',
                      active ? 'bg-surface-sunken' : '',
                      severityFilter === 'all' ? 'font-semibold text-primary-600 dark:text-primary-400' : 'text-gray-700 dark:text-gray-200',
                    )}
                  >
                    <span>{t('All')}</span>
                    <span className="text-gray-400">{scopedFindings.length}</span>
                  </button>
                )}
              </Menu.Item>
              {severityOrder.map((sev) => {
                const count = scopedFindings.filter((f) => f.severity === sev).length;
                if (count === 0) return null;
                return (
                  <Menu.Item key={sev}>
                    {({ active }: { active: boolean }) => (
                      <button
                        onClick={() => setSeverityFilter(sev)}
                        className={clsx(
                          'w-full text-start px-3 py-1.5 text-xs flex items-center justify-between',
                          active ? 'bg-surface-sunken' : '',
                          severityFilter === sev ? 'font-semibold text-primary-600 dark:text-primary-400' : 'text-gray-700 dark:text-gray-200',
                        )}
                      >
                        <div className="flex items-center gap-1.5">
                          <Badge color={severityColors[sev]}>{severityLabels[sev]}</Badge>
                        </div>
                        <span className="text-gray-400">{count}</span>
                      </button>
                    )}
                  </Menu.Item>
                );
              })}
            </Menu.Items>
          </Menu>

          {/* Decision dropdown */}
          <Menu as="div">
            <Menu.Button className="flex items-center gap-1 px-2 py-1 border border-hairline rounded text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-surface-sunken transition-colors">
              <span>{decisionFilter === 'all' ? t('Decision') : decisionFilter === 'pending' ? t('Pending') : formatLabel(decisionFilter)}</span>
              <PiCaretDownLight className="w-3 h-3 text-gray-400" />
            </Menu.Button>
            <Menu.Items
              anchor="bottom start"
              transition
              className="z-[300] [--anchor-gap:4px] w-44 bg-surface rounded-xl shadow-xl ring-1 ring-black/[0.05] dark:ring-white/[0.08] py-1 origin-top-left transition duration-150 ease-out data-[closed]:opacity-0 data-[closed]:scale-95"
            >
              {(['all', 'pending', 'accepted', 'rejected', 'modified', 'escalated'] as const).map((df) => {
                const count = df === 'all' ? scopedFindings.length :
                  df === 'pending' ? scopedFindings.filter(f => !f.decision).length :
                  scopedFindings.filter(f => f.decision === df).length;
                if (count === 0 && df !== 'all' && df !== 'pending') return null;
                return (
                  <Menu.Item key={df}>
                    {({ active }: { active: boolean }) => (
                      <button
                        onClick={() => setDecisionFilter(df)}
                        className={clsx(
                          'w-full text-start px-3 py-1.5 text-xs flex items-center justify-between',
                          active ? 'bg-surface-sunken' : '',
                          decisionFilter === df ? 'font-semibold text-primary-600 dark:text-primary-400' : 'text-gray-700 dark:text-gray-200',
                        )}
                      >
                        <span>{df === 'all' ? t('All') : df === 'pending' ? t('Pending') : formatLabel(df)}</span>
                        <span className="text-gray-400">{count}</span>
                      </button>
                    )}
                  </Menu.Item>
                );
              })}
            </Menu.Items>
          </Menu>

          {/* Clear filters */}
          {(severityFilter !== 'all' || decisionFilter !== 'all') && (
            <button
              onClick={() => { setSeverityFilter('all'); setDecisionFilter('all'); }}
              className="px-1.5 py-0.5 rounded text-[11px] text-gray-400 hover:text-gray-600 hover:bg-surface-sunken transition-colors"
              title={t('Clear filters')}
            >
              {t('Clear')}
            </button>
          )}
        </div>

        {/* Bulk action bar */}
        {!isProcessing && !decisionsLocked && pendingFilteredFindings.length > 0 && (
          <div className="flex-shrink-0 border-b border-hairline px-2.5 py-1.5">
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] text-gray-400 me-0.5">
                {pendingFilteredFindings.length} {t('pending')}
              </span>
              <button
                onClick={() => handleBulkDecision('accepted')}
                className="flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium text-success-700 dark:text-success-400 bg-success-50 dark:bg-success-900/30 hover:bg-success-100 dark:hover:bg-success-900/50 transition-colors"
              >
                <PiCheckCircleLight className="w-3 h-3" />
                {t('Accept All')}
              </button>
              <button
                onClick={() => handleBulkDecision('rejected')}
                className="flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium text-danger-700 dark:text-danger-400 bg-danger-50 dark:bg-danger-900/30 hover:bg-danger-100 dark:hover:bg-danger-900/50 transition-colors"
              >
                <PiXCircleLight className="w-3 h-3" />
                {t('Reject All')}
              </button>
            </div>
          </div>
        )}

        {/* Document completion CTA */}
        {selectedDocumentId !== 'all' && docAllDecided && !decisionsLocked && (
          <div className="flex-shrink-0 border-b border-success-200 dark:border-success-800 bg-success-50 dark:bg-success-900/20 px-3 py-2.5">
            <div className="flex items-center gap-1.5 mb-1.5">
              <PiCheckCircleLight className="w-3.5 h-3.5 text-success-500 flex-shrink-0" />
              <span className="text-xs font-medium text-success-700 dark:text-success-300">
                {t('All findings in this document reviewed')}
              </span>
            </div>
            {nextPendingDocId === null ? (
              <p className="text-xs text-success-600/70 dark:text-success-400/70">
                {t('All documents complete')}
              </p>
            ) : (
              <button
                onClick={() => {
                  setSelectedDocumentId(nextPendingDocId);
                  setSelectedFindingUuid(null);
                }}
                className="w-full flex items-center justify-between px-2.5 py-1.5 bg-success-100 dark:bg-success-900/40 hover:bg-success-200 dark:hover:bg-success-900/60 rounded-md transition-colors text-xs font-medium text-success-700 dark:text-success-300"
              >
                <span>{t('Next Document')}</span>
                <PiArrowRightLight className="w-3.5 h-3.5 rtl:-scale-x-100" />
              </button>
            )}
          </div>
        )}

        {/* Findings list - liquid glass cards */}
        <div ref={findingsListRef} className="flex-1 overflow-y-auto py-2 px-1.5 custom-scrollbar-main">
          {/*
            A RUN THAT IS RUNNING SAYS SO - before the first finding, not after.

            The order here matters and used to be wrong. `isLoadingFindings`
            was checked first, so a starting run drew a four-row SKELETON
            (which promises rows that are not coming yet), and only once that
            settled did it fall through to a small loader in a fixed `h-32`
            box inside a column several hundred pixels tall.

            A live run now takes precedence over both: the shared panel, from
            t=0, driven by the session's lifecycle rather than by the arrival
            of findings. The skeleton is kept for the case it was written for —
            re-fetching the list of an already-finished session.
          */}
          {/* ABOVE the list, not instead of it: findings stream in underneath
              while the run is still going, and the panel stays until the
              session stops reporting itself as processing. */}
          {isProcessing && (
            <div className="px-2 pb-3">
              <AIProgressPanel
                run={{ ...reviewRun, elapsedSeconds: reviewElapsed }}
                title={t('Analysing documents…')}
                completedTitle={t('Analysis complete')}
                failedTitle={t('Analysis failed')}
                showPercent={false}
                subItemsDisplay="list"
                hint={
                  findings.length === 0 ? (
                    <p className="text-xs leading-relaxed text-fg-muted">
                      {t('Findings appear here as each document is read.')}
                    </p>
                  ) : undefined
                }
              />
            </div>
          )}

          {isLoadingFindings && findings.length === 0 && !isProcessing ? (
            <Skeleton variant="list" rows={4} />
          ) : filteredFindings.length === 0 ? (
            /* Silent while a run is live - the panel above is already saying
               why the list is empty, and saying it twice reads as a fault. */
            isProcessing ? null : (
              <div className="flex h-32 flex-col items-center justify-center text-sm text-fg-muted">
                <PiFileTextLight className="w-8 h-8 mb-2" />
                {t('No findings')}
              </div>
            )
          ) : (
            filteredFindings.map((finding) => (
              /*
                A row, not a <button>: it carries the "show in document"
                control, and a button inside a button is markup no browser
                agrees on. The role/tabIndex/keydown trio puts back exactly
                what the element gave up - click, focus ring, Enter and Space.
              */
              <div
                key={finding.uuid}
                role="button"
                tabIndex={0}
                data-testid="review-finding-row"
                data-active={selectedFindingUuid === finding.uuid}
                onClick={() => selectFinding(finding)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    selectFinding(finding);
                  }
                }}
                className={clsx(
                  LIST_ROW, 'cursor-pointer focus:outline-none',
                  'focus-visible:ring-1 focus-visible:ring-champagne',
                  selectedFindingUuid === finding.uuid ? LIST_ROW_ACTIVE : LIST_ROW_IDLE
                )}
              >
                <span
                  aria-hidden
                  className={clsx(
                    LIST_RAIL,
                    selectedFindingUuid === finding.uuid ? 'opacity-100' : 'opacity-0'
                  )}
                />
                <div className="flex items-start gap-2">
                  <span className={clsx(
                    'mt-[5px] w-2 h-2 rounded-full flex-shrink-0',
                    finding.severity === 'critical' ? 'bg-danger-500' :
                    finding.severity === 'major' ? 'bg-warning-400' :
                    finding.severity === 'minor' ? 'bg-warning-400' : 'bg-info-400',
                  )} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-1.5 mb-0.5">
                      {/*
                        THE TITLE IS THE JUMP.

                        A locator hidden behind a small icon is a thing you
                        have to be told about; the clause name is the thing a
                        reviewer already points at when they mean "that one,
                        in the document". So the name carries the action - a
                        real <button>, focusable, named for what it does - and
                        the crosshair stays only as the visible hint that the
                        name is clickable at all.

                        `flex-1` so the title eats the slack and the badge
                        stays pinned right. `stopPropagation` keeps the row's
                        own select from firing underneath it.
                      */}
                      {finding.document_id ? (
                        <button
                          type="button"
                          data-testid="finding-locate"
                          title={t('Scroll to this section in the document')}
                          aria-label={t('Scroll to this section in the document')}
                          onClick={(e) => { e.stopPropagation(); locateFinding(finding); }}
                          className={clsx(
                            'flex-1 min-w-0 flex items-center gap-1 text-start cursor-pointer',
                            'text-[13px] font-medium text-fg-strong leading-snug',
                            'rounded-sm transition-colors duration-200',
                            'hover:text-champagne focus:outline-none',
                            'focus-visible:ring-1 focus-visible:ring-champagne',
                          )}
                        >
                          <span className="truncate underline decoration-dotted decoration-hairline underline-offset-[3px] group-hover:decoration-champagne">
                            {formatLabel(finding.clause_type)}
                          </span>
                          <PiCrosshairSimpleLight
                            aria-hidden
                            className={clsx(
                              'flex-shrink-0 w-3.5 h-3.5 transition-opacity duration-200',
                              'opacity-0 group-hover:opacity-70',
                              selectedFindingUuid === finding.uuid && 'opacity-70',
                            )}
                          />
                        </button>
                      ) : (
                        /* No document behind it - nothing to scroll to, so the
                           name stays a name rather than a dead control. */
                        <span className="flex-1 min-w-0 text-[13px] font-medium text-fg-strong truncate leading-snug">
                          {formatLabel(finding.clause_type)}
                        </span>
                      )}
                      {finding.decision ? (
                        <span className={clsx(
                          'flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full',
                          finding.decision === 'accepted' ? 'bg-success-50 dark:bg-success-900/30 text-success-600 dark:text-success-400' :
                          finding.decision === 'rejected' ? 'bg-danger-50 dark:bg-danger-900/30 text-danger-600 dark:text-danger-400' :
                          finding.decision === 'modified' ? 'bg-warning-50 dark:bg-warning-900/30 text-warning-600 dark:text-warning-400' :
                          'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
                        )}>
                          {formatLabel(finding.decision)}
                        </span>
                      ) : (
                        <span className="w-1.5 h-1.5 mt-1 rounded-full bg-warning-400 flex-shrink-0" />
                      )}
                    </div>
                    {finding.issue_description && (
                      <p className="text-[11px] text-fg-muted line-clamp-2 leading-relaxed">
                        {finding.issue_description}
                      </p>
                    )}
                    {/*
                      The provenance line: which document, which clause,
                      how sure the model is.

                      The document name shows ONLY in the all-documents
                      view. Scoped to one document it would repeat the
                      same filename down every row, which is noise; across
                      documents its absence made two identically-named
                      clause types indistinguishable.
                    */}
                    {(finding.section_reference
                      || (selectedDocumentId === 'all' && finding.document_name)
                      || confidenceBand(finding.confidence_score)) && (
                      <div className="mt-0.5 flex items-center gap-1.5 min-w-0">
                        {selectedDocumentId === 'all' && finding.document_name && (
                          <span
                            className="text-[10px] text-fg-muted truncate min-w-0"
                            title={finding.document_name}
                          >
                            {finding.document_name}
                          </span>
                        )}
                        {selectedDocumentId === 'all' && finding.document_name
                          && finding.section_reference && (
                          <span className="text-fg-muted/60 flex-shrink-0" aria-hidden>·</span>
                        )}
                        {finding.section_reference && (
                          <span className="text-[10px] text-fg-muted truncate min-w-0">
                            {finding.section_reference}
                          </span>
                        )}
                        {(() => {
                          const band = confidenceBand(finding.confidence_score);
                          if (!band) return null;
                          return (
                            <span
                              data-testid="finding-row-confidence"
                              data-band={band}
                              title={`${t('Confidence')}: ${t(CONFIDENCE_LABEL[band])}`}
                              className={clsx(
                                'ms-auto flex-shrink-0 text-[9px] font-semibold uppercase',
                                'tracking-wider leading-none',
                                CONFIDENCE_TONE[band],
                              )}
                            >
                              {t(CONFIDENCE_LABEL[band])}
                            </span>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
              </>
              )}
            </div>
  );

  /*
    ── Reading mode ──────────────────────────────────────────────────

    Expanding does NOT render a different tree, and that is the whole point.

    It used to early-return a second `DetailFrame`, which meant React tore
    down the workspace and built a new one on every toggle: the document
    viewer unmounted, its blob and converted HTML went with it, and the
    stream reopened. On this backend that is six to nineteen seconds of
    skeleton - pressing expand appeared to hang, and pressing it again while
    it hung made a second round trip. That is the "gets stuck / doesn't
    expand properly".

    Now the same element simply changes box: `display: contents` while
    docked, so it adds nothing to the layout, and a fixed full-viewport flex
    column while expanded. The DOM node is stable, so the iframe, the blob
    and the scroll position all survive the toggle, and the toggle itself
    costs one class change. Idempotent by construction, and there is no
    intermediate state to get stuck in.
  */

  return (<>
    <DetailFrame
      breadcrumbs={breadcrumbs}
      actions={headerActions}
      dropdownActions={headerDropdownActions}
      collaborators={headerCollaborators}
      additionalHeaderElements={
        <>
          {/* The risk readout moved to the toolbar row below, next to the
              findings toggle - one header row, and each group of controls
              where its meaning puts it. */}
          <ProjectBadges resourceType="review_session" resourceUuid={reviewUuid} />
          <PendingAccessRequests resourceType="review_session" resourceUuid={reviewUuid} />
        </>
      }
    >


      {/* Processing indicator - subtle inline bar */}
      {isProcessing && (
        <div className="flex-shrink-0 border-b border-hairline-soft glass-bar px-5 py-2">
          <div className="flex items-center gap-3">
            <BrandLoader size="xs" className="!h-3.5 !w-3.5 flex-shrink-0" />
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {t('Analyzing')} {documentsCompleted}/{documentsTotal} {t('documents')}
            </span>
            <div className="flex-1 h-1 bg-surface-sunken rounded-full overflow-hidden max-w-[180px]">
              <div
                className="h-full bg-info-400/70 rounded-full transition-all duration-700 ease-out"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <span className="text-xs text-gray-400 dark:text-gray-500 tabular-nums">
              {findings.length} {t('findings')}
            </span>
          </div>
        </div>
      )}

      {/* Finalized banner */}
      {isFinalized && (
        <div className="flex-shrink-0 border-b border-success-200 dark:border-success-800 bg-success-50 dark:bg-success-900/20 px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <PiSealCheckLight className="w-5 h-5 text-success-600 dark:text-success-400" />
              <div>
                <span className="text-sm font-medium text-success-700 dark:text-success-300">
                  {t('Review Finalized')}
                </span>
                {currentSession?.finalized_at && (
                  <span className="text-xs text-success-600/70 dark:text-success-400/70 ms-2">
                    {formatUtcToLocal(currentSession.finalized_at)}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleDownloadFinal}
                variant='primary'
                size='small'
                icon={<PiDownloadSimpleLight className="w-3.5 h-3.5" />}
              >
                {t('Download Final')}
              </Button>
              {isOwner && (
                <Button
                  onClick={handleReopenReview}
                  variant='outline'
                  size='small'
                  icon={<PiArrowCounterClockwiseLight className="w-3.5 h-3.5" />}
                >
                  {t('Reopen')}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Playbook-updated banner (Epic B) - scoped re-review of changed clauses.
          Not while finalized or actively processing. */}
      {!isFinalized && !isProcessing && (
        <ReviewPlaybookBanner sessionUuid={reviewUuid} />
      )}

      {/* All decided banner - shown when all findings reviewed but review not yet completed */}
      {allDecided && !isProcessing && !isSignOffPending && !isFinalized && (currentSession?.status === 'completed' || currentSession?.status === 'completed_with_errors') && (
        <div className="flex-shrink-0 border-b border-success-200 dark:border-success-800 bg-success-50 dark:bg-success-900/20 px-6 py-2.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <PiCheckCircleLight className="w-4.5 h-4.5 text-success-600 dark:text-success-400" />
              <span className="text-sm font-medium text-success-700 dark:text-success-300">
                {t('All findings reviewed — ready to complete')}
              </span>
            </div>
            <Button
              onClick={handleCompleteReview}
              variant='primary'
              size='small'
              icon={<PiFlagCheckeredLight className="w-3.5 h-3.5" />}
            >
              {t('Complete Review')}
            </Button>
          </div>
        </div>
      )}

      {/* Pending sign-off banner */}
      {isSignOffPending && !isFinalized && (
        <div className="flex-shrink-0 border-b border-warning-200 dark:border-warning-800 bg-warning-50 dark:bg-warning-900/20 px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <PiSealCheckLight className="w-5 h-5 text-warning-600 dark:text-warning-400" />
              <div>
                <span className="text-sm font-medium text-warning-700 dark:text-warning-300">
                  {t('All findings reviewed — awaiting sign-off')}
                </span>
                {signOffStatus && (
                  <span className="text-xs text-warning-600/70 dark:text-warning-400/70 ms-2">
                    {signOffStatus.signed_count}/{signOffStatus.required_count} {t('signed')}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Sign-off progress - profile images with status ring */}
              {signOffStatus?.signers && (
                <div className="flex items-center -space-x-1.5 me-2">
                  {signOffStatus.signers.map((signer) => {
                    // Cross-reference with collaborators for avatar/email/initials
                    // Through `accessModel`, not `collab.avatar_url` alone —
                    // the session payload spells the picture differently from
                    // the sign-off payload, and reading one key left signers
                    // with a grey silhouette next to their status ring.
                    const collab = currentSession?.collaborators?.find((c) => c.user_id === signer.user_uuid);
                    const person = collab ? normalise(collab) : null;
                    const initials =
                      person?.initials ||
                      signer.user_name?.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase() ||
                      '?';
                    return (
                      <div
                        key={signer.user_uuid}
                        className={clsx(
                          'rounded-full border-2',
                          signer.signed_off
                            ? 'border-success-400'
                            : 'border-hairline opacity-60',
                        )}
                        title={`${signer.user_name} - ${signer.signed_off ? t('Signed off') : t('Pending')}`}
                      >
                        <ProfileImage
                          profilePicture={person?.avatar || null}
                          email={person?.email || null}
                          initials={initials}
                          name={signer.user_name}
                          size="w-6 h-6"
                        />
                      </div>
                    );
                  })}
                </div>
              )}
              {canSignOff && (
                <Button
                  onClick={handleSignOff}
                  variant='primary'
                  size='small'
                  icon={<PiSealCheckLight className="w-3.5 h-3.5" />}
                >
                  {t('Sign Off')}
                </Button>
              )}
              {hasSignedOff && (
                <span className="flex items-center gap-1 text-xs text-success-600 dark:text-success-400 font-medium">
                  <PiCheckCircleLight className="w-4 h-4" />
                  {t('You signed off')}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/*
        ── The one header row ──────────────────────────────────────────

        [ findings toggle · risk · counts · progress ]
                    [ document name ▾ · Original/Revised ]
                                        [ download · expand ]

        Three groups, each answering a different question: where the SESSION
        stands, which DOCUMENT you are reading, and what you can DO with it.
        They used to be scattered - the readout up in the breadcrumb bar, the
        name and mode toggles crowded left with the download and expand, and
        two pane pills on the right - so nothing in the bar was where its
        meaning suggested it should be.
      */}
      {/*
        The workspace - one element, two boxes. See "Reading mode" above.
      */}
      <div
        data-testid="review-workspace"
        data-expanded={isDocumentExpanded}
        className={clsx(
          isDocumentExpanded
            ? 'fixed inset-0 z-[60] flex flex-col bg-bg'
            : 'contents'
        )}
      >
      {/*
        A GRID, not three flex children.

        As flex, the left group was `flex-1 min-w-0` but the readout inside it
        is built of `flex-shrink-0` parts, so below about 1400px the box
        shrank and its CONTENTS did not: they overflowed to the right and
        painted straight across the document's name - measured at 261px of
        overlap, with "21 decided · 166 pending" sitting on top of the
        filename and making it ambiguous what a click on the badge would hit.

        Grid columns cannot overlap. The outer two are `minmax(0,1fr)` and
        clip; the middle is `auto`, so the document name - the thing this bar
        is most about - gets the width it needs and truncates rather than
        colliding.
      */}
      <div
        className="flex-shrink-0 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3
                   border-b border-hairline-soft glass-bar px-4 h-11"
        data-testid="review-document-toolbar"
      >
        {/* LEFT - the session: is it bad, and how far through am I. */}
        <div className="flex items-center gap-3 min-w-0 overflow-hidden">
          <PaneRail
            panels={panels}
            ariaLabel={t('Panels')}
            /* Straight from the surface config: the way back to the findings
               after they have been closed, and the way to what the session
               cost. */
            items={reviewRailItems({
              t,
              icons: {
                findings: <PiListChecksLight />,
                aiUsage: <PiSparkleLight />,
              },
            })}
          />
          {currentSession && (
            <ReviewHeaderReadout
              riskScore={currentSession.overall_risk_score}
              severity={{
                critical: currentSession.critical_count,
                major: currentSession.major_count,
                minor: currentSession.minor_count,
                info: currentSession.info_count,
              }}
              buckets={buckets}
              labels={{
                risk: t('Risk'),
                decided: t('decided'),
                escalated: t('escalated'),
                pending: t('pending'),
                critical: t('Critical'),
                major: t('Major'),
                minor: t('Minor'),
                info: t('Info'),
              }}
            />
          )}
        </div>

        {/* CENTRE - WHICH document. Nothing else: the name is the one thing
            in this bar that changes what you are looking at, and it reads
            better alone than leading a row of controls. */}
        <div className="flex items-center min-w-0 justify-self-center">
          {documentPicker}
        </div>

        {/*
          RIGHT - everything you do TO the document, in one group.

          The version toggles used to sit in the centre beside the name,
          which split "what am I reading" from "what can I do with it"
          across two places and left the right-hand end holding two lone
          icons. Reading mode, revision style, saving it and expanding it
          are all the same kind of act, so they are one cluster with a
          hairline between the toggles and the icon buttons.
        */}
        <div className="flex items-center justify-end gap-2 min-w-0">
          {documentModeToggles}
          {documentActions()}
        </div>
      </div>
      {/*
        The session surface: a full-bleed CANVAS carrying THE DOCUMENT, with
        glass panes floating over its edges - the findings on the left, the
        list of documents on the right.

        The canvas used to carry the selected finding's detail, which is why
        this page could show you the same finding twice and the document not
        at all. The panes dock either side, stack, reorder by dragging their
        header, resize from their inner edge, and persist per user under the
        scoped layout store.
      */}
      <ReviewSurface
        layoutApi={panels}
        /* Expanded, the surface fills the fixed column it is in; docked, it
           takes what the page chrome leaves. */
        height={isDocumentExpanded ? undefined : `calc(100vh - ${CHROME_PX + bannerOffset}px)`}
        panes={[
          {
            id: 'findings',
            title: (
              <span className="flex items-center gap-1.5">
                {t('Findings')}
                {filteredFindings.length > 0 && (
                  <span className="text-[10px] text-fg-muted tabular-nums">
                    {filteredFindings.length === findings.length
                      ? findings.length
                      : `${filteredFindings.length} / ${findings.length}`}
                  </span>
                )}
              </span>
            ),
            icon: <PiListChecksLight />,
            /*
              WHICH SIDE. The pane can already be dragged between docks by its
              header, but a drag is a gesture you have to guess at - so the
              same move gets a button, next to the close it sits beside. One
              press, and it persists with the rest of the layout.
            */
            actions: (
              <button
                type="button"
                data-testid="findings-flip-side"
                onClick={() => panels.move('findings', findingsDock === 'right' ? 'left' : 'right', 0)}
                title={findingsDock === 'right' ? t('Move to the left') : t('Move to the right')}
                aria-label={findingsDock === 'right' ? t('Move to the left') : t('Move to the right')}
                className="p-1 rounded-md text-fg-muted hover:bg-surface-sunken/70
                           hover:text-champagne transition-colors duration-200 ease-slide"
              >
                {findingsDock === 'right'
                  ? <PiArrowLineLeftLight className="w-3.5 h-3.5 rtl:-scale-x-100" />
                  : <PiArrowLineRightLight className="w-3.5 h-3.5 rtl:-scale-x-100" />}
              </button>
            ),
            body: findingsPaneBody,
          },
          {
            id: 'aiUsage',
            title: t('AI usage'),
            icon: <PiSparkleLight />,
            /* A thunk: the panel fetches on mount, so a session nobody asks
               the cost of never costs a request. */
            body: () => (
              <AIUsagePanel
                entityType="review"
                entityId={reviewUuid}
                entityName={currentSession?.name || ''}
                onClose={() => panels.setVisible('aiUsage', false)}
              />
            ),
          },
        ]}
      >
        {/*
          ── The canvas: THE DOCUMENT ──────────────────────────────────

          A review session is a document with objections to it, so the
          document is what the middle of the page shows - roomy, selectable,
          and never sharing the space with a finding.

          It used to render the SELECTED FINDING's detail here, which put the
          same finding on screen twice: once in the middle, once in the list
          beside it. The detail moved into the findings pane, where the list
          it belongs to already was, and the document took the space it had
          been denied.
        */}
        <ReviewDocumentCanvas
          sessionUuid={reviewUuid}
          target={centerDocument}
          viewSource={viewSource}
          revisionStyle={revisionStyle}
          revisionToken={revisionToken}
          onFindingSelect={handleFindingSelectFromDocument}
        />
      </ReviewSurface>
      </div>

    </DetailFrame>

    {/* Activity log - fixed overlay (matches site-wide pattern) */}
    {showAuditLog && reviewUuid && (
      <ActivityDrawer onClose={() => setShowAuditLog(false)}>
        <AuditLogPanel
          entityType="review"
          entityId={reviewUuid}
          entityName={currentSession?.name || ''}
          onClose={() => setShowAuditLog(false)}
        />
      </ActivityDrawer>
    )}

    <FeatureTour
      steps={reviewSessionTourSteps}
      run={isTourRunning}
      onCallback={handleTourCallback}
      continuous
      showProgress
      showSkipButton
      disableOverlay
    />
  </>);
};

export default ReviewSessionDetail;
